import React, { useEffect, useState, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useParams, useNavigate, useSearchParams } from "react-router-dom";
import AdminDashboard from "./Admin";
import HeroRenderer from "./components/HeroRenderer";

// Use relative API path for production compatibility
// In production, /api/* routes to backend; in dev, REACT_APP_BACKEND_URL provides the full URL
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL && BACKEND_URL.trim() ? `${BACKEND_URL}/api` : '/api';

// Debug: Log API configuration (remove after confirming it works)
if (typeof window !== 'undefined') {
  console.log('[API Config] BACKEND_URL:', BACKEND_URL, '| API:', API);
}

// ============== API HOOKS ==============
const usePageContent = (pageKey) => {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`${API}/pages/${pageKey}`);
        if (response.ok) {
          const data = await response.json();
          setContent(data.content || {});
        } else {
          console.error(`Error fetching ${pageKey}: ${response.status}`);
          setContent({});
        }
      } catch (error) {
        console.error(`Error fetching ${pageKey} content:`, error);
        setContent({});
      }
      setLoading(false);
    };
    fetchContent();
  }, [pageKey]);

  return { content, loading };
};

const useCharacters = () => {
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCharacters = async () => {
      try {
        const response = await fetch(`${API}/characters`);
        const data = await response.json();
        setCharacters(data);
      } catch (error) {
        console.error('Error fetching characters:', error);
      }
      setLoading(false);
    };
    fetchCharacters();
  }, []);

  return { characters, loading };
};

const useMemberships = () => {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMemberships = async () => {
      try {
        const response = await fetch(`${API}/memberships`);
        const data = await response.json();
        setMemberships(data);
      } catch (error) {
        console.error('Error fetching memberships:', error);
      }
      setLoading(false);
    };
    fetchMemberships();
  }, []);

  return { memberships, loading };
};

const useProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${API}/shop/products`);
        const data = await response.json();
        setProducts(data);
      } catch (error) {
        console.error('Error fetching products:', error);
      }
      setLoading(false);
    };
    fetchProducts();
  }, []);

  return { products, loading };
};

const useHomepageSections = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSections = async () => {
      try {
        const response = await fetch(`${API}/homepage-sections`);
        const data = await response.json();
        setSections(data);
      } catch (error) {
        console.error('Error fetching homepage sections:', error);
      }
      setLoading(false);
    };
    fetchSections();
  }, []);

  return { sections, loading };
};

// Hook for cinematic hero media settings
const useHeroMedia = () => {
  const [heroMedia, setHeroMedia] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHeroMedia = async () => {
      try {
        const response = await fetch(`${API}/hero-media`);
        const data = await response.json();
        setHeroMedia(data);
      } catch (error) {
        console.error('Error fetching hero media:', error);
      }
      setLoading(false);
    };
    fetchHeroMedia();
  }, []);

  return { heroMedia, loading };
};

// ============== NAVIGATION ==============
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { content: siteSettings } = usePageContent('site_settings');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/', label: 'The Story' },
    { path: '/characters', label: 'Meet the Characters' },
    { path: '/membership', label: 'Membership' },
    { path: '/shop', label: 'Shop' },
    { path: '/about', label: 'About' },
    { path: '/author', label: 'About the Author' },
    { path: '/faq', label: 'FAQ' },
    { path: '/reviews', label: 'Reviews' },
    { path: '/free-letter-kit', label: 'Free Kit' },
  ];

  // Clean nav styles for light theme
  const goldGlowStyle = {
    textShadow: 'none'
  };
  
  const goldGlowHoverStyle = {
    textShadow: 'none'
  };

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        scrolled 
          ? 'py-4 shadow-md' 
          : 'py-6'
      }`}
      style={{
        background: scrolled 
          ? 'rgba(250,247,242,0.97)'
          : 'rgba(250,247,242,0.92)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo with gold glow */}
        <Link 
          to="/" 
          className="font-serif text-xl tracking-wide transition-all duration-300 hover:scale-105"
          data-testid="nav-logo"
          style={{ 
            color: '#2D2926',
            fontWeight: '500',
            letterSpacing: '0.05em',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#5C4A3D';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#2D2926';
          }}
        >
          {siteSettings.site_name || 'The Timeless Post'}
        </Link>
        
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="font-serif text-sm transition-all duration-300"
                style={{ 
                  color: isActive ? '#2D2926' : '#5C4A3D',
                  fontWeight: isActive ? '600' : '500',
                  letterSpacing: '0.08em',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#2D2926';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = isActive ? '#2D2926' : '#5C4A3D';
                }}
              >
                {link.label}
              </Link>
            );
          })}
          {/* Sign In Button with enhanced styling */}
          <Link
            to="/signin"
            data-testid="nav-signin"
            className="font-serif text-sm px-5 py-2 border rounded transition-all duration-300"
            style={{
              color: '#2D2926',
              fontWeight: '500',
              letterSpacing: '0.05em',
              borderColor: 'rgba(45, 41, 38, 0.3)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(176,144,96,0.1)';
              e.currentTarget.style.borderColor = 'rgba(45, 41, 38, 0.5)';
              e.currentTarget.style.color = '#5C4A3D';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.borderColor = 'rgba(45, 41, 38, 0.3)';
              e.currentTarget.style.color = '#2D2926';
            }}
          >
            Sign In
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden p-2 transition-all duration-300"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-button"
          style={{ 
            color: '#2D2926',
          }}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {mobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      <div 
        className={`md:hidden absolute top-full left-0 right-0 backdrop-blur-md transition-all duration-500 overflow-hidden ${
          mobileMenuOpen ? 'max-h-[500px] border-b border-gold-500/30' : 'max-h-0'
        }`}
        style={{
          background: 'linear-gradient(to bottom, rgba(250,247,242,0.98), rgba(245,239,230,0.95))'
        }}
      >
        <div className="px-6 py-4 space-y-4">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                className="block font-serif py-2 transition-colors"
                style={{
                  color: isActive ? '#2D2926' : '#5C4A3D',
                  fontWeight: isActive ? '600' : '500',
                  letterSpacing: '0.05em',
                }}
              >
                {link.label}
              </Link>
            );
          })}
          <Link
            to="/signin"
            className="block font-serif text-center py-3 border rounded transition-all mt-4"
            style={{
              color: '#2D2926',
              fontWeight: '500',
              borderColor: 'rgba(45, 41, 38, 0.3)',
            }}
          >
            Sign In
          </Link>
        </div>
      </div>
    </nav>
  );
};

// ============== VINTAGE FLOURISH DIVIDER ==============
const VintageFlourish = ({ className = '' }) => (
  <div className={`flex items-center justify-center py-6 ${className}`} data-testid="vintage-flourish">
    <div className="flex items-center gap-3 w-full max-w-xs opacity-40">
      <div className="flex-1 h-px bg-gradient-to-r from-transparent to-gold-500" />
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="text-gold-500 flex-shrink-0">
        <path d="M12 2L13.09 8.26L18 6L14.74 10.91L21 12L14.74 13.09L18 18L13.09 15.74L12 22L10.91 15.74L6 18L9.26 13.09L3 12L9.26 10.91L6 6L10.91 8.26L12 2Z" fill="currentColor" opacity="0.6"/>
      </svg>
      <div className="flex-1 h-px bg-gradient-to-l from-transparent to-gold-500" />
    </div>
  </div>
);

// ============== SITE FOOTER COMPONENT ==============
const SiteFooter = ({ siteSettings }) => {
  const [footerLinks, setFooterLinks] = useState([]);
  const API = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    const fetchFooterLinks = async () => {
      try {
        const response = await fetch(`${API}/api/footer-links`);
        if (response.ok) {
          const data = await response.json();
          setFooterLinks(data);
        }
      } catch (error) {
        console.error('Failed to fetch footer links:', error);
      }
    };
    fetchFooterLinks();
  }, [API]);

  // Default policy links (always shown)
  const defaultPolicyLinks = [
    { label: 'Terms & Conditions', url: '/terms-and-conditions' },
    { label: 'Privacy Policy', url: '/privacy-policy' },
    { label: 'Refund Policy', url: '/refund-policy' },
  ];

  // Combine default links with dynamic CMS links
  const allPolicyLinks = [
    ...defaultPolicyLinks,
    ...footerLinks.map(link => ({ label: link.label, url: link.url }))
  ];

  return (
    <footer className="bg-charcoal-900 border-t border-gold-500/5 py-12 mt-auto">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* Column 1: Navigation */}
          <div>
            <h4 className="font-display text-parchment-100 text-sm mb-4">Explore</h4>
            <ul className="space-y-2">
              <li><Link to="/about" className="font-serif text-parchment-400 text-sm hover:text-gold-400 transition-colors">About</Link></li>
              <li><Link to="/characters" className="font-serif text-parchment-400 text-sm hover:text-gold-400 transition-colors">Characters</Link></li>
              <li><Link to="/membership" className="font-serif text-parchment-400 text-sm hover:text-gold-400 transition-colors">Subscriptions</Link></li>
              <li><Link to="/gift" className="font-serif text-parchment-400 text-sm hover:text-gold-400 transition-colors">Gift a Subscription</Link></li>
              <li><Link to="/reviews" className="font-serif text-parchment-400 text-sm hover:text-gold-400 transition-colors">Testimonials</Link></li>
            </ul>
          </div>
          
          {/* Column 2: Policies & CMS Pages */}
          <div>
            <h4 className="font-display text-parchment-100 text-sm mb-4">Information</h4>
            <ul className="space-y-2">
              {allPolicyLinks.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.url} 
                    className="font-serif text-parchment-400 text-sm hover:text-gold-400 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Column 3: Contact */}
          <div>
            <h4 className="font-display text-parchment-100 text-sm mb-4">Contact</h4>
            <ul className="space-y-2">
              <li>
                <a href={`mailto:${siteSettings?.contact_email || 'letters@the-timeless-post.com'}`} className="font-serif text-gold-400 text-sm hover:text-gold-300 transition-colors">
                  {siteSettings?.contact_email || 'letters@the-timeless-post.com'}
                </a>
              </li>
              <li><span className="font-serif text-parchment-400 text-sm">{siteSettings?.location || 'Based in Australia'}</span></li>
            </ul>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="border-t border-gold-500/5 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="font-display text-lg text-parchment-100 hover:text-gold-400 transition-colors">
            {siteSettings?.site_name || 'The Timeless Post'}
          </Link>
          <p className="font-serif text-parchment-500 text-sm">
            © {new Date().getFullYear()} {siteSettings?.site_name || 'The Timeless Post'}. All stories are eternal.
          </p>
        </div>
      </div>
    </footer>
  );
};

// ============== SCROLL REVEAL HOOK ==============
const useScrollReveal = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [threshold]);

  return [ref, isVisible];
};

// ============== DIAMOND SYMBOL ==============
const DiamondSymbol = ({ className = "", size = "small", symbolUrl }) => {
  const sizeClasses = {
    small: "w-8 h-8",
    medium: "w-12 h-12",
    large: "w-16 h-16"
  };
  
  const defaultUrl = "https://customer-assets.emergentagent.com/job_cinematic-reveal-1/artifacts/s98b9jeq_E3A3E006-2664-4FCF-A35E-42C4D7B2969D.png";
  
  return (
    <img 
      src={symbolUrl || defaultUrl}
      alt=""
      className={`${sizeClasses[size]} object-contain opacity-60 ${className}`}
      aria-hidden="true"
    />
  );
};

// ============== CHARACTER REVEAL ==============
const CharacterReveal = ({ character, isVisible }) => {
  return (
    <div
      className={`transition-all duration-[2000ms] ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'
      }`}
    >
      <div className="max-w-2xl mx-auto text-center">
        <p className="font-serif text-gold-400/70 tracking-[0.4em] text-xs mb-4 uppercase">
          {character.short_bio}
        </p>
        <h3 className="font-display text-3xl md:text-4xl lg:text-5xl text-parchment-100 mb-6">
          {character.name}
        </h3>
        {character.long_backstory && (
          <div 
            className="font-serif text-parchment-300/90 text-lg md:text-xl leading-relaxed italic"
            dangerouslySetInnerHTML={{ __html: character.long_backstory.substring(0, 200) + '...' }}
          />
        )}
      </div>
    </div>
  );
};

// ============== CINEMATIC HERO MEDIA COMPONENT ==============
const CinematicHeroMedia = ({ heroMedia }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  
  const {
    enabled,
    media_type,
    video_url,
    slideshow_images = [],
    slideshow_interval = 5000,
    show_frame = true,
    frame_style = 'antique_gold',
    text_overlay = {},
    show_cta = false,
    cta_text = '',
    cta_link = '/membership'
  } = heroMedia || {};

  // Slideshow auto-advance
  useEffect(() => {
    if (media_type !== 'slideshow' || slideshow_images.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slideshow_images.length);
    }, slideshow_interval);
    return () => clearInterval(interval);
  }, [media_type, slideshow_images.length, slideshow_interval]);

  if (!enabled) return null;

  const getFrameStyles = () => {
    switch (frame_style) {
      case 'antique_gold':
        return 'border-8 border-double border-amber-600/60 shadow-[inset_0_0_30px_rgba(180,130,70,0.3),0_0_60px_rgba(180,130,70,0.2)] rounded-sm';
      case 'dark_wood':
        return 'border-[12px] border-gold-600/30 shadow-[inset_0_0_20px_rgba(176,144,96,0.15),0_0_40px_rgba(176,144,96,0.1)] rounded';
      case 'simple':
        return 'border-4 border-gold-500/40 shadow-xl rounded-sm';
      default:
        return 'border-8 border-double border-amber-600/60 shadow-[inset_0_0_30px_rgba(180,130,70,0.3)]';
    }
  };

  const FrameOrnaments = () => (
    <>
      <div className="absolute -top-1 -left-1 w-8 h-8 border-t-2 border-l-2 border-amber-500/60 z-30" />
      <div className="absolute -top-1 -right-1 w-8 h-8 border-t-2 border-r-2 border-amber-500/60 z-30" />
      <div className="absolute -bottom-1 -left-1 w-8 h-8 border-b-2 border-l-2 border-amber-500/60 z-30" />
      <div className="absolute -bottom-1 -right-1 w-8 h-8 border-b-2 border-r-2 border-amber-500/60 z-30" />
    </>
  );

  const handleCTAClick = () => {
    if (cta_link) {
      if (cta_link.startsWith('http')) {
        window.open(cta_link, '_blank');
      } else {
        window.location.href = cta_link;
      }
    }
  };

  return (
    <div className="relative w-full" data-testid="cinematic-hero-media">
      {/* Frame container — NO overflow-hidden, NO fixed height */}
      <div className={`relative ${show_frame ? getFrameStyles() : ''}`}>
        {show_frame && frame_style === 'antique_gold' && <FrameOrnaments />}
        
        {/* Media content — image drives the height naturally */}
        <div className="relative w-full bg-charcoal-900">
          {media_type === 'video' && video_url ? (
            <video
              src={video_url}
              autoPlay
              loop
              muted
              playsInline
              onLoadedData={() => setIsLoaded(true)}
              className={`w-full h-auto block transition-opacity duration-1000 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
              style={{ objectFit: 'contain' }}
            />
          ) : slideshow_images.length > 0 ? (
            <div className="relative w-full">
              {/* First slide as layout driver */}
              <img
                src={slideshow_images[0]?.url}
                alt={slideshow_images[0]?.alt || 'Slide 1'}
                className={`w-full h-auto block transition-opacity duration-[2000ms] ease-in-out ${
                  currentSlide === 0 ? 'opacity-100' : 'opacity-0'
                }`}
                style={{ objectFit: 'contain' }}
                onLoad={() => setIsLoaded(true)}
              />
              {/* Remaining slides layered on top */}
              {slideshow_images.slice(1).map((img, i) => {
                const index = i + 1;
                return (
                  <img
                    key={index}
                    src={img.url}
                    alt={img.alt || `Slide ${index + 1}`}
                    className={`absolute top-0 left-0 w-full h-auto block transition-opacity duration-[2000ms] ease-in-out ${
                      index === currentSlide ? 'opacity-100' : 'opacity-0'
                    }`}
                    style={{ objectFit: 'contain' }}
                  />
                );
              })}
              
              {/* Slide indicators */}
              {slideshow_images.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                  {slideshow_images.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        index === currentSlide ? 'bg-gold-400 w-6' : 'bg-gold-400/40 hover:bg-gold-400/60'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="py-20 flex items-center justify-center">
              <p className="text-parchment-500 italic">No media configured</p>
            </div>
          )}
          
          {/* Loading spinner */}
          {!isLoaded && slideshow_images.length > 0 && (
            <div className="absolute inset-0 flex items-center justify-center bg-charcoal-900">
              <div className="w-8 h-8 border-2 border-gold-400/30 border-t-gold-400 rounded-full animate-spin" />
            </div>
          )}
          
          {/* Vignette overlay */}
          <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,transparent_50%,rgba(245,239,230,0.5)_100%)]" />
        </div>
      </div>

      {/* CTA Button — rendered OUTSIDE the image container, always visible */}
      {show_cta && cta_text && (
        <div className="flex justify-center py-6" data-testid="hero-cta-wrapper">
          <button
            onClick={handleCTAClick}
            data-testid="hero-cta-button"
            className="px-10 py-3.5 bg-gradient-to-r from-gold-600 to-gold-500 hover:from-gold-500 hover:to-gold-400 text-parchment-100 font-display text-sm tracking-widest uppercase rounded transition-all duration-300 shadow-lg hover:shadow-gold-600/20"
          >
            {cta_text}
          </button>
        </div>
      )}

    </div>
  );
};

// ============== HOMEPAGE SECTION COMPONENTS ==============
// Hero section rendering is now handled by HeroRenderer (imported from ./components/HeroRenderer)

const ProphecySection = ({ content }) => {
  const [ref, isVisible] = useScrollReveal(0.2);
  return (
    <section ref={ref} className="relative py-32 md:py-48 bg-charcoal-900" data-testid="prophecy-section">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <div className={`transition-all duration-[2000ms] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex justify-center mb-12">
            <DiamondSymbol size="small" />
          </div>
          <blockquote className="font-display text-2xl md:text-3xl lg:text-4xl text-parchment-100/95 leading-relaxed mb-10 italic">
            "{content.quote || 'When ink meets parchment under the light of believing hearts, stories become doorways...'}"
          </blockquote>
          <p className="font-serif text-parchment-400/70 text-sm tracking-wider">
            {content.attribution || '— The First Binding'}
          </p>
          <div className="flex justify-center mt-12">
            <DiamondSymbol size="small" />
          </div>
        </div>
      </div>
    </section>
  );
};

const ComingSoonSection = ({ content }) => {
  const [ref, isVisible] = useScrollReveal(0.2);
  return (
    <section ref={ref} className="py-20 md:py-28 bg-charcoal-900">
      <div className={`max-w-2xl mx-auto px-6 text-center transition-all duration-[2000ms] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <DiamondSymbol size="small" className="mx-auto mb-8" />
        <p className="font-serif text-parchment-300/80 text-base md:text-lg leading-relaxed italic">
          {content.heading || 'More voices are stirring in the mist...'} <br />
          {content.description || 'New characters are preparing their first letters to you.'}
        </p>
      </div>
    </section>
  );
};

const CTASection = ({ content }) => {
  const [ref, isVisible] = useScrollReveal(0.2);
  return (
    <section ref={ref} className="relative py-32 md:py-48 overflow-hidden bg-gradient-to-b from-charcoal-900 via-charcoal-800/50 to-charcoal-900" data-testid="cta-section">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-gold-500 rounded-full filter blur-[200px]" />
      </div>
      <div className={`relative z-10 max-w-2xl mx-auto px-6 text-center transition-all duration-[2000ms] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <DiamondSymbol size="medium" className="mx-auto mb-10" />
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-parchment-100 mb-6 leading-tight">
          {content.heading || 'The Story Awaits'}
        </h2>
        <p className="font-serif text-parchment-300/90 text-lg leading-relaxed mb-12 max-w-lg mx-auto">
          {content.description || 'Step through the threshold. Let the characters write to you. Become part of a story that has waited for your arrival.'}
        </p>
        <div className="flex flex-col sm:flex-row gap-5 justify-center">
          <Link to={content.button_link || "/membership"} data-testid="cta-enter-story" className="group inline-flex items-center justify-center gap-3 bg-gold-500 hover:bg-gold-400 text-parchment-100 font-serif text-base px-10 py-4 rounded transition-all duration-500 hover:shadow-[0_0_50px_rgba(212,165,116,0.25)]">
            <span>{content.button_text || 'Enter the Story'}</span>
            <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
          <Link to="/characters" data-testid="cta-meet-characters" className="inline-flex items-center justify-center gap-3 border border-gold-500/30 hover:border-gold-400/60 text-parchment-100 hover:text-gold-400 font-serif text-base px-10 py-4 rounded transition-all duration-500">
            Meet the Characters
          </Link>
        </div>
      </div>
    </section>
  );
};

const AtmosphereSection = ({ content }) => {
  const [ref, isVisible] = useScrollReveal(0.1);
  const heightClass = {
    small: 'h-[300px]',
    medium: 'h-[500px]',
    large: 'h-[700px]',
    full: 'min-h-screen'
  }[content.height || 'medium'];

  return (
    <section ref={ref} className={`relative ${heightClass} overflow-hidden`}>
      {content.image_url && (
        <div className="absolute inset-0">
          <img src={content.image_url} alt={content.caption || ''} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal-900/30 via-transparent to-charcoal-900/30" />
        </div>
      )}
      {content.overlay_text && (
        <div className={`absolute inset-0 flex items-center justify-center transition-all duration-[2000ms] ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          <p className="font-display text-3xl md:text-5xl lg:text-6xl text-parchment-100 text-center px-6 drop-shadow-2xl">
            {content.overlay_text}
          </p>
        </div>
      )}
      {content.caption && (
        <div className="absolute bottom-6 left-0 right-0 text-center">
          <p className="font-serif text-parchment-400/70 text-sm italic">{content.caption}</p>
        </div>
      )}
    </section>
  );
};

const FeaturedCardsSection = ({ content }) => {
  const [ref, isVisible] = useScrollReveal(0.2);
  const cards = content.cards || [];

  return (
    <section ref={ref} className="py-24 md:py-32 bg-charcoal-900">
      <div className="max-w-7xl mx-auto px-6">
        {(content.section_label || content.heading) && (
          <div className={`text-center mb-16 transition-all duration-[2000ms] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {content.section_label && (
              <p className="font-serif text-gold-400 tracking-[0.3em] text-xs mb-4">{content.section_label}</p>
            )}
            {content.heading && (
              <h2 className="font-display text-4xl md:text-5xl text-parchment-100 mb-6">{content.heading}</h2>
            )}
            {content.description && (
              <p className="font-serif text-parchment-300 text-lg max-w-2xl mx-auto">{content.description}</p>
            )}
          </div>
        )}
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${Math.min(cards.length, 3)} gap-8 transition-all duration-[2000ms] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {cards.map((card, index) => (
            <div key={index} className="group relative bg-charcoal-800/50 rounded-lg overflow-hidden border border-gold-500/10 hover:border-gold-500/30 transition-all duration-500">
              {card.image_url && (
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={card.image_url} alt={card.title || ''} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
              )}
              <div className="p-6">
                {card.title && (
                  <h3 className="font-display text-xl text-parchment-100 mb-2">{card.title}</h3>
                )}
                {card.text && (
                  <p className="font-serif text-parchment-300/80 text-sm leading-relaxed mb-4">{card.text}</p>
                )}
                {card.link_url && (
                  <Link to={card.link_url} className="inline-flex items-center gap-2 text-gold-400 hover:text-gold-300 font-serif text-sm transition-colors">
                    {card.link_text || 'Learn More'}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============== IMAGE GALLERY SECTION ==============
const GallerySection = ({ content }) => {
  const [ref, isVisible] = useScrollReveal(0.2);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const galleryRef = useRef(null);
  
  const images = content.images || [];
  const minSwipeDistance = 50;

  const goToPrevious = () => {
    setCurrentIndex(prev => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex(prev => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) goToNext();
    if (isRightSwipe) goToPrevious();
  };

  if (images.length === 0) return null;

  return (
    <section ref={ref} className="py-24 md:py-32 bg-charcoal-900" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-6">
        {content.heading && (
          <div className={`text-center mb-12 transition-all duration-[2000ms] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h2 className="font-display text-3xl md:text-4xl text-parchment-100">{content.heading}</h2>
          </div>
        )}
        
        <div 
          ref={galleryRef}
          className={`relative transition-all duration-[2000ms] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Main Image Container */}
          <div className="relative aspect-[16/9] md:aspect-[21/9] overflow-hidden rounded-sm bg-charcoal-800">
            {images.map((img, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-opacity duration-700 ${index === currentIndex ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
              >
                <img
                  src={img.url}
                  alt={img.caption || `Gallery image ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                {img.caption && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-charcoal-900/90 to-transparent p-6">
                    <p className="font-serif text-parchment-300 text-sm md:text-base text-center">{img.caption}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={goToPrevious}
                className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-charcoal-900/60 hover:bg-charcoal-900/80 border border-gold-500/20 hover:border-gold-500/40 rounded-full transition-all duration-300 group"
                aria-label="Previous image"
              >
                <svg className="w-5 h-5 md:w-6 md:h-6 text-gold-400 group-hover:text-gold-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={goToNext}
                className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-charcoal-900/60 hover:bg-charcoal-900/80 border border-gold-500/20 hover:border-gold-500/40 rounded-full transition-all duration-300 group"
                aria-label="Next image"
              >
                <svg className="w-5 h-5 md:w-6 md:h-6 text-gold-400 group-hover:text-gold-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          {/* Dot Indicators */}
          {images.length > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${index === currentIndex ? 'bg-gold-400 w-6' : 'bg-gold-500/30 hover:bg-gold-500/50'}`}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// ============== SLIDESHOW SECTION ==============
const SlideshowSection = ({ content }) => {
  const [ref, isVisible] = useScrollReveal(0.2);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  
  const images = content.images || [];
  const autoplay = content.autoplay !== false;
  const autoplayInterval = (content.autoplay_interval || 4) * 1000;
  const showArrows = content.show_arrows !== false;
  const showDots = content.show_dots !== false;

  // Autoplay effect
  useEffect(() => {
    if (!autoplay || isPaused || images.length <= 1) return;
    
    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev === images.length - 1 ? 0 : prev + 1));
    }, autoplayInterval);
    
    return () => clearInterval(timer);
  }, [autoplay, isPaused, images.length, autoplayInterval]);

  const goToPrevious = () => {
    setCurrentIndex(prev => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex(prev => (prev === images.length - 1 ? 0 : prev + 1));
  };

  if (images.length === 0) return null;

  return (
    <section 
      ref={ref} 
      className="py-16 md:py-24 bg-charcoal-900" 
      data-testid="slideshow-section"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* Title and Description */}
        {(content.title || content.description) && (
          <div className={`text-center mb-10 transition-all duration-[2000ms] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {content.title && (
              <h2 className="font-display text-2xl md:text-4xl text-parchment-100 mb-3">{content.title}</h2>
            )}
            {content.description && (
              <p className="font-serif text-parchment-400 max-w-2xl mx-auto">{content.description}</p>
            )}
          </div>
        )}
        
        {/* Slideshow Container with Victorian Frame */}
        <div 
          className={`relative transition-all duration-[2000ms] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          {/* Outer Frame */}
          <div className="relative bg-charcoal-700 p-2 md:p-3" style={{ boxShadow: '0 4px 20px rgba(176,144,96,0.08), inset 0 1px 0 rgba(176,144,96,0.15)' }}>
            {/* Inner Border */}
            <div className="relative border border-gold-500/20">
              {/* Image Container - 16:9 Aspect Ratio */}
              <div className="relative aspect-[16/9] overflow-hidden bg-charcoal-900">
                {images.map((img, index) => (
                  <div
                    key={index}
                    className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                      index === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
                    }`}
                  >
                    <img
                      src={img.url}
                      alt={img.alt || `Slide ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
                
                {/* Subtle Vignette Overlay */}
                <div className="absolute inset-0 pointer-events-none" style={{
                  background: 'radial-gradient(ellipse at center, transparent 50%, rgba(245,239,230,0.3) 100%)'
                }} />
              </div>

              {/* Navigation Arrows */}
              {showArrows && images.length > 1 && (
                <>
                  <button
                    onClick={goToPrevious}
                    className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-charcoal-900/70 hover:bg-charcoal-900/90 border border-gold-500/30 hover:border-gold-500/50 transition-all duration-300 group z-20"
                    aria-label="Previous slide"
                  >
                    <svg className="w-5 h-5 md:w-6 md:h-6 text-gold-400 group-hover:text-gold-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={goToNext}
                    className="absolute right-3 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-charcoal-900/70 hover:bg-charcoal-900/90 border border-gold-500/30 hover:border-gold-500/50 transition-all duration-300 group z-20"
                    aria-label="Next slide"
                  >
                    <svg className="w-5 h-5 md:w-6 md:h-6 text-gold-400 group-hover:text-gold-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Dot Indicators - Outside Frame */}
          {showDots && images.length > 1 && (
            <div className="flex justify-center gap-3 mt-6">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`transition-all duration-500 ${
                    index === currentIndex 
                      ? 'w-8 h-2 bg-gold-400' 
                      : 'w-2 h-2 bg-gold-500/30 hover:bg-gold-500/50'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// ============== EXPANDABLE NARRATIVE SECTION ==============
const ExpandableNarrativeSection = ({ content }) => {
  const [ref, isVisible] = useScrollReveal(0.2);
  const [isExpanded, setIsExpanded] = useState(false);
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);
  
  const enableExpand = content.enable_expand !== false;
  const previewLines = content.preview_lines || 10;
  const lineHeight = 1.9;
  const previewHeight = previewLines * lineHeight;
  const hasBackgroundImage = content.background_image_url && content.background_image_url.trim();

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [content.body]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  if (!content.body) return null;

  return (
    <section 
      ref={ref} 
      className="relative overflow-hidden"
      data-testid="expandable-narrative-section"
      style={{ 
        paddingTop: '100px', 
        paddingBottom: '100px',
        minHeight: '500px'
      }}
    >
      {/* Background Layer - Image or Fallback */}
      {hasBackgroundImage ? (
        <>
          {/* Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ 
              backgroundImage: `url(${content.background_image_url})`,
            }}
          />
          {/* Very light vignette — keeps image mostly visible */}
          <div 
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(to bottom, rgba(250,247,242,0.15) 0%, rgba(250,247,242,0.1) 50%, rgba(250,247,242,0.15) 100%)'
            }}
          />
        </>
      ) : (
        /* Fallback: Plain dark background with subtle texture */
        <>
          <div className="absolute inset-0 bg-charcoal-900" />
          <div className="absolute inset-0 opacity-5" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C8A24A' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </>
      )}
      
      {/* Top Fade Edge - Blends into previous section */}
      <div 
        className="absolute top-0 left-0 right-0 h-24 pointer-events-none z-10"
        style={{
          background: 'linear-gradient(to bottom, rgba(250,247,242,1) 0%, transparent 100%)'
        }}
      />
      
      {/* Bottom Fade Edge - Blends into next section */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none z-10"
        style={{
          background: 'linear-gradient(to top, rgba(250,247,242,1) 0%, transparent 100%)'
        }}
      />
      
      {/* Content Container */}
      <div className="max-w-[700px] mx-auto px-6 md:px-8 relative z-20">
        
        {/* Soft parchment text backdrop — rounded with blur */}
        {hasBackgroundImage && (
          <div 
            className="absolute inset-0 -inset-x-8 -inset-y-6 rounded-2xl"
            style={{
              background: 'rgba(250, 247, 240, 0.78)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
              boxShadow: '0 0 60px 20px rgba(250, 247, 240, 0.4)',
            }}
          />
        )}
        
        <div className="relative">
        {/* Top Decorative Divider */}
        <div className={`text-center mb-12 transition-all duration-[1500ms] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <span 
            className="text-gold-600/70 tracking-[0.5em] text-sm font-light"
          >
            ✧ ✧ ✧
          </span>
        </div>

        {/* Heading */}
        {content.heading && (
          <div className={`text-center mb-12 transition-all duration-[1500ms] delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 
              className={`font-display text-2xl md:text-3xl lg:text-4xl tracking-wide ${hasBackgroundImage ? 'text-parchment-100' : 'text-parchment-100'}`}
            >
              {content.heading}
            </h2>
          </div>
        )}

        {/* Narrative Content */}
        <div 
          className={`relative transition-all duration-[1500ms] delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div 
            className="relative overflow-hidden transition-all duration-700 ease-in-out"
            style={{
              maxHeight: enableExpand && !isExpanded ? `${previewHeight}em` : `${contentHeight + 100}px`,
            }}
          >
            <div 
              ref={contentRef}
              className={`font-serif text-base md:text-lg text-center leading-[1.9] 
                prose max-w-none
                prose-p:mb-6 
                prose-strong:font-normal
                prose-em:italic
                prose-headings:font-display
                [&>p]:first-letter:text-2xl [&>p]:first-letter:font-display
                ${hasBackgroundImage 
                  ? 'text-parchment-300 prose-p:text-parchment-300 prose-strong:text-gold-700 prose-em:text-parchment-200 prose-headings:text-parchment-100 [&>p]:first-letter:text-gold-700'
                  : 'text-parchment-200 prose-invert prose-p:text-parchment-200/95 prose-strong:text-gold-400 prose-em:text-parchment-100 prose-headings:text-parchment-100 [&>p]:first-letter:text-gold-400'
                }`}
              style={{ letterSpacing: '0.02em' }}
              dangerouslySetInnerHTML={{ __html: content.body }}
            />
          </div>

          {/* Soft Fade Overlay when collapsed */}
          {enableExpand && !isExpanded && (
            <div 
              className="absolute bottom-0 left-0 right-0 h-40 pointer-events-none transition-opacity duration-500"
              style={{
                background: hasBackgroundImage 
                  ? 'linear-gradient(to bottom, transparent 0%, rgba(250,247,240,0.5) 40%, rgba(250,247,240,0.95) 100%)'
                  : 'linear-gradient(to bottom, transparent 0%, rgba(250,247,242,0.5) 30%, rgba(250,247,242,0.9) 70%, rgba(250,247,242,1) 100%)'
              }}
            />
          )}
        </div>

        {/* "Read the invitation" Toggle */}
        {enableExpand && (
          <div className={`text-center mt-10 transition-all duration-[1500ms] delay-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            <button
              onClick={toggleExpand}
              className="group inline-flex flex-col items-center gap-3 px-8 py-4 transition-all duration-500 hover:transform hover:scale-105"
              data-testid="narrative-toggle-btn"
            >
              <span 
                className={`font-serif tracking-widest text-sm uppercase transition-colors duration-300 ${hasBackgroundImage ? 'text-gold-700 hover:text-gold-600' : 'text-gold-400/90 hover:text-gold-300'}`}
              >
                {isExpanded ? '· Fold the letter ·' : '· Read the invitation ·'}
              </span>
              <div className="flex items-center gap-3 opacity-60 group-hover:opacity-100 transition-opacity duration-300">
                <div className={`h-px bg-gold-600/60 transition-all duration-500 ${isExpanded ? 'w-4' : 'w-10'}`} />
                <div className={`transition-transform duration-500 ${isExpanded ? 'rotate-180' : 'rotate-0'}`}>
                  <svg className={`w-3 h-3 ${hasBackgroundImage ? 'text-gold-700/80' : 'text-gold-500/80'}`} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 17.414L3.293 8.707l1.414-1.414L12 14.586l7.293-7.293 1.414 1.414L12 17.414z"/>
                  </svg>
                </div>
                <div className={`h-px bg-gold-600/60 transition-all duration-500 ${isExpanded ? 'w-4' : 'w-10'}`} />
              </div>
            </button>
          </div>
        )}

        {/* Bottom Decorative Divider */}
        <div className={`text-center mt-14 transition-all duration-[1500ms] delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <span className="text-gold-600/70 tracking-[0.5em] text-sm font-light">
            ✧ ✧ ✧
          </span>
        </div>
        </div>{/* close relative wrapper */}
      </div>
    </section>
  );
};

// ============== HOW IT WORKS SECTION ==============
const HowItWorksSection = ({ content }) => {
  const [ref, isVisible] = useScrollReveal(0.1);
  const steps = content.steps && content.steps.length > 0 ? content.steps : [];
  const bgColor = content.bg_color || '#FAF7F2';
  const markerStyle = content.marker_style || 'seal';
  const markerSize = content.marker_size || 64;
  const timelineColor = content.timeline_color || '#C7A15A';
  const bgImageUrl = content.bg_image_url || '';
  const bgImageOpacity = (content.bg_image_opacity ?? 15) / 100;
  const bgOverlayColor = content.bg_overlay_color || '';
  const bgImageEnabled = content.bg_image_enabled !== false && bgImageUrl;

  // Detect if background is dark or light for text color adaptation
  const isDark = (() => {
    const hex = bgColor.replace('#', '');
    if (hex.length < 6) return false;
    const r = parseInt(hex.substr(0,2), 16), g = parseInt(hex.substr(2,2), 16), b = parseInt(hex.substr(4,2), 16);
    return (r * 0.299 + g * 0.587 + b * 0.114) < 140;
  })();
  const headingColor = isDark ? '#F5EFE6' : '#2D2926';
  const textColor = isDark ? 'rgba(200,190,175,0.85)' : '#5C4A3D';
  const subtitleColor = isDark ? 'rgba(199,161,90,0.7)' : '#9A7B4F';
  const markerBg = isDark ? bgColor : '#FAF7F2';

  const renderMarker = (index, style) => {
    const num = index + 1;
    const sz = `${markerSize}px`;
    if (style === 'compass') {
      return (
        <div className="rounded-full border-2 flex items-center justify-center relative" style={{width: sz, height: sz, borderColor: `${timelineColor}66`, backgroundColor: markerBg, boxShadow: `0 0 15px ${timelineColor}22`}}>
          <svg className="absolute w-full h-full" style={{color: `${timelineColor}22`}} viewBox="0 0 64 64"><circle cx="32" cy="32" r="30" fill="none" stroke="currentColor" strokeWidth="0.5"/><circle cx="32" cy="32" r="24" fill="none" stroke="currentColor" strokeWidth="0.3" strokeDasharray="3 3"/></svg>
          <span className="font-display text-lg md:text-xl relative z-10" style={{color: timelineColor}}>{num}</span>
        </div>
      );
    }
    if (style === 'stamp') {
      return (
        <div className="border-2 border-dashed flex items-center justify-center rounded-sm" style={{width: sz, height: sz, borderColor: `${timelineColor}55`, backgroundColor: markerBg, boxShadow: `0 0 12px ${timelineColor}1A`}}>
          <span className="font-display text-xl md:text-2xl" style={{color: timelineColor}}>{num}</span>
        </div>
      );
    }
    return (
      <div className="rounded-full flex items-center justify-center relative" style={{width: sz, height: sz, background: `radial-gradient(circle at 40% 35%, ${timelineColor} 0%, ${timelineColor}AA 60%, ${timelineColor}77 100%)`, boxShadow: `0 2px 8px rgba(0,0,0,0.3), inset 0 1px 2px rgba(255,255,255,0.2), 0 0 20px ${timelineColor}33`}}>
        <span className="font-display text-lg md:text-xl font-bold drop-shadow-sm" style={{color: markerBg}}>{num}</span>
      </div>
    );
  };

  const renderStep = (step, index) => {
    const stepLayout = step.layout || (index % 2 === 0 ? 'text_left' : 'text_right');
    const textOnLeft = stepLayout === 'text_left' || stepLayout === 'content_left_image_right';
    const imgAlt = step.image_alt || step.title || '';

    const textBlock = (align) => (
      <div className={align === 'right' ? 'text-right pr-2' : 'pl-2'}>
        {step.subtitle && <p className="font-serif tracking-[0.2em] text-[10px] uppercase mb-2" style={{color: subtitleColor}}>{step.subtitle}</p>}
        <h3 className="font-display text-xl lg:text-2xl mb-3 leading-tight" style={{color: headingColor}}>{step.title}</h3>
        <p className="font-serif text-sm leading-[1.8]" style={{color: textColor}}>{step.description}</p>
        {step.button_text && (
          <div className={`mt-4 ${align === 'right' ? 'flex justify-end' : ''}`}>
            <Link to={step.button_link || '/membership'} className="font-serif text-xs border px-5 py-2 rounded transition-all" style={{color: timelineColor, borderColor: `${timelineColor}44`}}>{step.button_text}</Link>
          </div>
        )}
      </div>
    );

    const imageBlock = (justify) => (
      step.image_url ? (
        <div className={`flex ${justify}`}><div className="w-full max-w-[300px] overflow-hidden rounded group"><img src={step.image_url} alt={imgAlt} className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" /></div></div>
      ) : <div />
    );

    return (
      <div key={index} className={`transition-all duration-[1200ms] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${300 + index * 200}ms` }}>
        {/* Mobile */}
        <div className="md:hidden">
          <div className="border rounded-lg overflow-hidden" style={{borderColor: `${timelineColor}15`, backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'}}>
            {step.image_url && (
              <div className="aspect-[16/9] overflow-hidden">
                <img src={step.image_url} alt={imgAlt} className="w-full h-full object-cover" loading="lazy" />
              </div>
            )}
            <div className="p-5 text-center">
              <div className="flex justify-center mb-4">{renderMarker(index, markerStyle)}</div>
              {step.subtitle && <p className="font-serif tracking-[0.2em] text-[10px] uppercase mb-2" style={{color: subtitleColor}}>{step.subtitle}</p>}
              <h3 className="font-display text-xl mb-3" style={{color: headingColor}}>{step.title}</h3>
              <p className="font-serif text-sm leading-relaxed" style={{color: textColor}}>{step.description}</p>
              {step.button_text && <Link to={step.button_link || '/membership'} className="inline-block mt-4 font-serif text-xs border px-5 py-2 rounded transition-all" style={{color: timelineColor, borderColor: `${timelineColor}44`}}>{step.button_text}</Link>}
            </div>
          </div>
        </div>
        {/* Desktop */}
        <div className="hidden md:grid md:grid-cols-[1fr_80px_1fr] md:gap-6 md:py-10 items-center">
          <div className={textOnLeft ? '' : 'order-3'}>
            {textOnLeft ? textBlock('right') : imageBlock('justify-end')}
          </div>
          <div className="flex justify-center relative z-10 order-2">{renderMarker(index, markerStyle)}</div>
          <div className={textOnLeft ? 'order-3' : ''}>
            {textOnLeft ? imageBlock('justify-start') : textBlock('left')}
          </div>
        </div>
      </div>
    );
  };

  return (
    <section ref={ref} className="py-16 md:py-28 relative overflow-hidden" style={{ backgroundColor: bgColor }} data-testid="how-it-works-section">
      {/* Background image */}
      {bgImageEnabled && <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${bgImageUrl})`, opacity: bgImageOpacity }} />}
      {/* Background overlay */}
      {bgOverlayColor && <div className="absolute inset-0" style={{ backgroundColor: bgOverlayColor }} />}
      {/* Subtle texture */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='200' height='200' viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")` }} />

      <div className="max-w-5xl mx-auto px-6 md:px-10 relative z-10">
        {/* Header */}
        <div className={`text-center mb-14 md:mb-20 transition-all duration-[1500ms] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {content.subtitle && <p className="font-serif tracking-[0.3em] text-[10px] md:text-xs uppercase mb-4" style={{color: subtitleColor}}>{content.subtitle}</p>}
          {content.heading && <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-wide mb-5" style={{color: headingColor}}>{content.heading}</h2>}
          {content.body && <div className="font-serif text-sm md:text-base leading-[1.8] max-w-2xl mx-auto" style={{color: textColor}} dangerouslySetInnerHTML={{ __html: content.body }} />}
        </div>

        {/* Timeline + Steps */}
        <div className="relative">
          <div className="absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2 hidden md:block" style={{background: `linear-gradient(to bottom, transparent, ${timelineColor}40 10%, ${timelineColor}40 90%, transparent)`}} />
          <div className="space-y-8 md:space-y-0">
            {steps.map((step, index) => renderStep(step, index))}
          </div>
        </div>

        {/* Bottom flourish */}
        <div className={`text-center mt-14 md:mt-20 transition-all duration-[1500ms] delay-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex items-center justify-center gap-4">
            <div className="h-px w-16" style={{background: `linear-gradient(to right, transparent, ${timelineColor}44)`}} />
            <span style={{color: `${timelineColor}77`}} className="tracking-[0.5em] text-sm font-light">&#10043;</span>
            <div className="h-px w-16" style={{background: `linear-gradient(to left, transparent, ${timelineColor}44)`}} />
          </div>
        </div>
      </div>
    </section>
  );
};

// ============== READER IMPRESSIONS SECTION ==============
const ReaderImpressionsSection = ({ content }) => {
  const [ref, isVisible] = useScrollReveal(0.15);
  
  const showDividers = content.show_dividers !== false;
  const testimonials = content.testimonials || [];
  const columns = content.columns || 3;

  if (testimonials.length === 0) return null;

  return (
    <section 
      ref={ref} 
      className="py-20 md:py-28 bg-charcoal-800 relative"
      data-testid="reader-impressions-section"
    >
      {/* Parchment texture overlay */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
      }} />
      
      <div className="max-w-[900px] mx-auto px-6 md:px-8 relative">
        
        {/* Top Decorative Divider */}
        {showDividers && (
          <div className={`text-center mb-12 transition-all duration-[1500ms] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="text-gold-500/60 tracking-[0.5em] text-sm font-light">✧ ✧ ✧</span>
          </div>
        )}

        {/* Heading */}
        {content.heading && (
          <div className={`text-center mb-12 transition-all duration-[1500ms] delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="font-display text-2xl md:text-3xl lg:text-4xl text-parchment-100 tracking-wide">
              {content.heading}
            </h2>
          </div>
        )}

        {/* Testimonials Grid */}
        <div className={`grid gap-6 ${columns === 2 ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              className={`relative transition-all duration-[1000ms] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${200 + index * 100}ms` }}
            >
              {/* Testimonial Card */}
              <div className="h-full bg-charcoal-900/50 border border-gold-500/10 rounded-lg p-6 
                hover:border-gold-500/25 transition-colors duration-500
                backdrop-blur-sm">
                
                {/* Quote Mark */}
                <div className="text-gold-500/20 text-4xl font-serif leading-none mb-3">"</div>
                
                {/* Title */}
                {testimonial.title && (
                  <h3 className="font-display text-parchment-100 text-lg mb-3 leading-snug">
                    {testimonial.title}
                  </h3>
                )}
                
                {/* Body */}
                <p className="font-serif text-parchment-400 text-sm leading-relaxed mb-4">
                  {testimonial.body}
                </p>
                
                {/* Reader Attribution */}
                {(testimonial.reader_name || testimonial.reader_location) && (
                  <div className="pt-4 border-t border-gold-500/5">
                    <p className="font-serif text-gold-400/80 text-sm italic">
                      {testimonial.reader_name && <span>— {testimonial.reader_name}</span>}
                      {testimonial.reader_name && testimonial.reader_location && <span className="text-parchment-500">, </span>}
                      {testimonial.reader_location && <span className="text-parchment-500">{testimonial.reader_location}</span>}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Footer Text */}
        {content.footer_text && (
          <div className={`text-center mt-12 transition-all duration-[1500ms] delay-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            <p className="font-serif text-parchment-500 text-sm italic">
              {content.footer_text}
            </p>
          </div>
        )}

        {/* Bottom Decorative Divider */}
        {showDividers && (
          <div className={`text-center mt-12 transition-all duration-[1500ms] delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="text-gold-500/60 tracking-[0.5em] text-sm font-light">✧ ✧ ✧</span>
          </div>
        )}
      </div>
    </section>
  );
};

// ============== FIRST LETTER PREVIEW SECTION ==============
const FirstLetterPreviewSection = ({ content }) => {
  const [ref, isVisible] = useScrollReveal(0.2);
  const navigate = useNavigate();
  
  const showDividers = content.show_dividers !== false;

  return (
    <section 
      ref={ref} 
      className="py-20 md:py-28 bg-charcoal-900 relative"
      data-testid="first-letter-preview-section"
    >
      {/* Subtle texture */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
      }} />
      
      <div className="max-w-[700px] mx-auto px-6 md:px-8 relative">
        
        {/* Top Decorative Divider */}
        {showDividers && (
          <div className={`text-center mb-12 transition-all duration-[1500ms] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="text-gold-500/60 tracking-[0.5em] text-sm font-light">✧ ✧ ✧</span>
          </div>
        )}

        {/* Heading */}
        {content.heading && (
          <div className={`text-center mb-6 transition-all duration-[1500ms] delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="font-display text-2xl md:text-3xl lg:text-4xl text-parchment-100 tracking-wide">
              {content.heading}
            </h2>
          </div>
        )}

        {/* Intro Text */}
        {content.intro_text && (
          <div className={`text-center mb-10 transition-all duration-[1500ms] delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <p className="font-serif text-parchment-400 text-base md:text-lg italic">
              {content.intro_text}
            </p>
          </div>
        )}

        {/* Letter Card */}
        <div className={`transition-all duration-[1500ms] delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="relative bg-parchment-100/[0.03] border border-gold-500/20 rounded-lg p-8 md:p-10
            backdrop-blur-sm shadow-lg">
            
            {/* Decorative corner flourishes */}
            <div className="absolute top-3 left-3 text-gold-500/30 text-lg">❧</div>
            <div className="absolute top-3 right-3 text-gold-500/30 text-lg rotate-180">❧</div>
            
            {/* Letter Content */}
            <div 
              className="font-serif text-parchment-200 text-base md:text-lg leading-[2] 
                prose prose-invert max-w-none
                prose-p:mb-5 prose-p:text-parchment-200/90 prose-p:indent-8
                [&>p:first-child]:indent-0
                prose-strong:text-parchment-100 
                prose-em:text-parchment-100"
              style={{ letterSpacing: '0.01em' }}
              dangerouslySetInnerHTML={{ __html: content.letter_excerpt }}
            />
            
            {/* Fade out effect at bottom */}
            <div 
              className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none rounded-b-lg"
              style={{
                background: 'linear-gradient(to bottom, transparent, rgba(250,247,242,0.95))'
              }}
            />
            
            {/* Character signature */}
            {content.character_name && (
              <div className="relative z-10 text-right mt-6 pr-4">
                <p className="font-serif text-gold-400/80 italic text-sm">
                  — {content.character_name}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Continuation Note */}
        {content.continuation_note && (
          <div className={`text-center mt-8 transition-all duration-[1500ms] delay-400 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            <p className="font-serif text-parchment-500 text-sm italic">
              {content.continuation_note}
            </p>
          </div>
        )}

        {/* CTA Button */}
        {content.show_cta && content.cta_text && (
          <div className={`text-center mt-8 transition-all duration-[1500ms] delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <button
              onClick={() => {
                if (content.cta_link) {
                  if (content.cta_link.startsWith('http')) {
                    window.location.href = content.cta_link;
                  } else {
                    navigate(content.cta_link);
                  }
                }
              }}
              className="group inline-flex items-center gap-3 px-8 py-4 
                bg-transparent border border-gold-500/50 rounded
                hover:bg-gold-500/10 hover:border-gold-500 
                transition-all duration-500"
              data-testid="letter-preview-cta"
            >
              <span className="font-display text-gold-400 tracking-wider text-sm uppercase">
                {content.cta_text}
              </span>
              <svg className="w-4 h-4 text-gold-500/70 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
          </div>
        )}

        {/* Bottom Decorative Divider */}
        {showDividers && (
          <div className={`text-center mt-12 transition-all duration-[1500ms] delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="text-gold-500/60 tracking-[0.5em] text-sm font-light">✧ ✧ ✧</span>
          </div>
        )}
      </div>
    </section>
  );
};

// ============== HOME PAGE ==============
const Home = () => {
  // Fetch from homepage_sections (managed by Homepage Builder in admin)
  const { sections } = useHomepageSections();
  const { content: siteSettings } = usePageContent('site_settings');
  const { heroMedia } = useHeroMedia();

  // Render a section based on its type
// ============== FEATURE SPOTLIGHT SECTION ==============
const FeatureSpotlightSection = ({ content }) => {
  const [ref, isVisible] = useScrollReveal(0.1);
  const bgColor = content.bg_color || '#FAF7F2';
  const spacingTop = content.spacing_top ?? 48;
  const spacingBottom = content.spacing_bottom ?? 48;
  const imageCorner = content.image_corner || 'square';
  const showFrame = content.show_frame === true;
  const showDivider = content.show_divider !== false;
  const sectionWidth = content.section_width || 'default';
  const bgImageUrl = content.bg_image_url || '';
  const bgImageEnabled = content.bg_image_enabled === true && bgImageUrl;
  const bgImageOpacity = (content.bg_image_opacity ?? 10) / 100;

  const isDark = (() => {
    const hex = (bgColor || '#FAF7F2').replace('#', '');
    if (hex.length < 6) return false;
    const r = parseInt(hex.substr(0,2),16), g = parseInt(hex.substr(2,2),16), b = parseInt(hex.substr(4,2),16);
    return (r*0.299+g*0.587+b*0.114) < 140;
  })();
  const headingColor = isDark ? '#F5EFE6' : '#2D2926';
  const textColor = isDark ? 'rgba(200,190,175,0.85)' : '#5C4A3D';
  const subtitleColor = isDark ? 'rgba(199,161,90,0.7)' : '#9A7B4F';
  const dividerColor = isDark ? 'rgba(199,161,90,0.4)' : 'rgba(199,161,90,0.35)';

  const maxW = sectionWidth === 'narrow' ? 'max-w-3xl' : sectionWidth === 'wide' ? 'max-w-6xl' : 'max-w-4xl';
  const imgRadius = imageCorner === 'rounded' ? 'rounded-lg' : imageCorner === 'vintage' ? 'rounded-sm' : '';

  return (
    <section
      ref={ref}
      data-testid="feature-spotlight-section"
      className="relative overflow-hidden"
      style={{ backgroundColor: bgColor, paddingTop: `${spacingTop}px`, paddingBottom: `${spacingBottom}px` }}
    >
      {bgImageEnabled && <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${bgImageUrl})`, opacity: bgImageOpacity }} />}
      <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='200' height='200' viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")` }} />

      <div className={`${maxW} mx-auto px-6 md:px-10 relative z-10`}>
        {/* Decorative divider */}
        {showDivider && (
          <div className={`text-center mb-8 transition-all duration-[1200ms] ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            <div className="flex items-center justify-center gap-4">
              <div className="h-px w-12" style={{background: `linear-gradient(to right, transparent, ${dividerColor})`}} />
              <span style={{color: dividerColor}} className="tracking-[0.5em] text-xs">&#10043;</span>
              <div className="h-px w-12" style={{background: `linear-gradient(to left, transparent, ${dividerColor})`}} />
            </div>
          </div>
        )}

        {/* Heading */}
        <div className={`text-center mb-8 md:mb-10 transition-all duration-[1500ms] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          {content.subtitle && (
            <p className="font-serif tracking-[0.25em] text-[10px] md:text-xs uppercase mb-3" style={{color: subtitleColor}}>{content.subtitle}</p>
          )}
          {content.title && (
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-wide leading-tight" style={{color: headingColor}}>{content.title}</h2>
          )}
        </div>

        {/* Featured Image */}
        {content.image_url && (
          <div className={`mb-8 md:mb-12 transition-all duration-[1500ms] delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className={`overflow-hidden group ${imgRadius} ${showFrame ? 'p-2 md:p-3 border bg-charcoal-800/5' : ''}`} style={showFrame ? {borderColor: dividerColor} : {}}>
              <img
                src={content.image_url}
                alt={content.image_alt || content.title || ''}
                className={`w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.02] ${imgRadius}`}
                loading="lazy"
              />
            </div>
            {content.image_caption && (
              <p className="font-serif text-xs mt-3 text-center italic" style={{color: subtitleColor}}>{content.image_caption}</p>
            )}
          </div>
        )}

        {/* Description */}
        {content.description && (
          <div className={`max-w-3xl mx-auto mb-8 transition-all duration-[1500ms] delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div
              className="font-serif text-base md:text-lg leading-[1.85] text-center prose max-w-none prose-p:mb-5 prose-strong:font-semibold prose-em:italic"
              style={{color: textColor}}
              dangerouslySetInnerHTML={{ __html: content.description }}
            />
          </div>
        )}

        {/* CTA Button */}
        {content.button_text && (
          <div className={`text-center transition-all duration-[1500ms] delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <Link
              to={content.button_link || '/membership'}
              className={`inline-block font-display text-xs md:text-sm tracking-widest uppercase transition-all duration-300 ${
                content.button_style === 'filled'
                  ? 'px-10 py-3.5 bg-gradient-to-r from-gold-600 to-gold-500 hover:from-gold-500 hover:to-gold-400 text-white rounded shadow-lg'
                  : 'px-8 py-3 border rounded hover:bg-charcoal-800/5'
              }`}
              style={content.button_style !== 'filled' ? {color: subtitleColor, borderColor: `${subtitleColor}55`} : {}}
            >
              {content.button_text}
            </Link>
          </div>
        )}

        {/* Bottom divider */}
        {showDivider && (
          <div className={`text-center mt-10 transition-all duration-[1200ms] delay-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            <div className="flex items-center justify-center gap-4">
              <div className="h-px w-12" style={{background: `linear-gradient(to right, transparent, ${dividerColor})`}} />
              <span style={{color: dividerColor}} className="tracking-[0.5em] text-xs">&#10043;</span>
              <div className="h-px w-12" style={{background: `linear-gradient(to left, transparent, ${dividerColor})`}} />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// ============== EDITORIAL STORY BLOCK ==============
const EditorialStoryBlock = ({ content }) => {
  const [ref, isVisible] = useScrollReveal(0.15);
  const layout = content.layout || 'image_left';
  const imageOnLeft = layout === 'image_left';
  const bgColor = content.bg_color || 'transparent';
  const spacingTop = content.spacing_top ?? 40;
  const spacingBottom = content.spacing_bottom ?? 40;
  const verticalAlign = content.vertical_align || 'center';
  const showFrame = content.show_frame === true;
  const blocks = content.blocks && content.blocks.length > 0
    ? content.blocks
    : [{ image_url: content.image_url, image_caption: content.image_caption, image_position: content.image_position, small_heading: content.small_heading, main_heading: content.main_heading, narrative: content.narrative, button_text: content.button_text, button_link: content.button_link, layout: content.layout }];

  const alignClass = verticalAlign === 'top' ? 'items-start' : verticalAlign === 'bottom' ? 'items-end' : 'items-center';

  return (
    <section
      ref={ref}
      data-testid="editorial-story-block"
      style={{ backgroundColor: bgColor === 'transparent' ? undefined : bgColor, paddingTop: `${spacingTop}px`, paddingBottom: `${spacingBottom}px` }}
      className="bg-charcoal-900"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10 space-y-16 md:space-y-24">
        {blocks.map((block, i) => {
          const blockImageLeft = (block.layout || layout) === 'image_left' ? (i % 2 === 0) : (i % 2 !== 0);
          const blockLayout = block.layout === 'alternate' ? (i % 2 === 0 ? true : false) : (block.layout || layout) === 'image_left';
          const imgLeft = block.layout === 'alternate' ? (i % 2 === 0) : blockLayout;

          return (
            <div
              key={i}
              className={`grid md:grid-cols-2 gap-8 md:gap-14 ${alignClass} transition-all duration-[1200ms] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${i * 200}ms` }}
            >
              {/* Image */}
              <div className={`${imgLeft ? 'md:order-1' : 'md:order-2'} order-1`}>
                {block.image_url ? (
                  <div className={`overflow-hidden ${showFrame ? 'p-2 border border-gold-500/20 bg-charcoal-800/30' : ''}`} style={{ borderRadius: showFrame ? '4px' : '2px' }}>
                    <img
                      src={block.image_url}
                      alt={block.main_heading || ''}
                      className="w-full h-auto object-cover"
                      style={{ objectPosition: block.image_position || 'center' }}
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="aspect-[4/5] bg-charcoal-800/30 border border-gold-500/10 flex items-center justify-center" style={{ borderRadius: '2px' }}>
                    <svg className="w-16 h-16 text-gold-500/20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  </div>
                )}
                {block.image_caption && (
                  <p className="font-serif text-parchment-400 text-xs mt-3 text-center italic">{block.image_caption}</p>
                )}
              </div>

              {/* Text */}
              <div className={`${imgLeft ? 'md:order-2' : 'md:order-1'} order-2 flex flex-col justify-center`}>
                {block.small_heading && (
                  <p className="font-serif text-gold-500 tracking-[0.25em] text-[10px] md:text-xs uppercase mb-3">{block.small_heading}</p>
                )}
                {block.main_heading && (
                  <h2 className="font-display text-2xl md:text-3xl lg:text-4xl text-parchment-100 mb-5 leading-tight">{block.main_heading}</h2>
                )}
                {block.narrative && (
                  <div
                    className="font-serif text-parchment-300 text-sm md:text-base leading-[1.85] prose max-w-none prose-p:mb-4 prose-p:text-parchment-300 prose-strong:text-parchment-200 prose-em:text-parchment-200"
                    dangerouslySetInnerHTML={{ __html: block.narrative }}
                  />
                )}
                {block.button_text && (
                  <div className="mt-6">
                    <Link
                      to={block.button_link || '/membership'}
                      className="inline-block font-serif text-sm text-gold-500 border border-gold-500/30 hover:border-gold-400 hover:text-gold-400 px-7 py-2.5 rounded transition-all duration-300 tracking-wide"
                    >
                      {block.button_text}
                    </Link>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

// ============== SCROLLING MARQUEE BANNER ==============
const ScrollingMarqueeBanner = ({ content }) => {
  const text = content.text || 'Unplug • Slow Down • Open An Envelope • Discover A New World • Read By Lamplight • Collect Stories • Escape The Scroll •';
  const bgColor = content.bg_color || '#17352D';
  const textColor = content.text_color || '#C7A15A';
  const speed = content.speed || 30;
  const fontSize = content.font_size || 18;
  const glowEnabled = content.glow !== false;
  const spacingTop = content.spacing_top ?? 0;
  const spacingBottom = content.spacing_bottom ?? 0;

  const glowStyle = glowEnabled ? {
    textShadow: `0 0 8px ${textColor}66, 0 0 20px ${textColor}33`
  } : {};

  // Duplicate text enough times to fill and overflow screen
  const repeated = `${text}  \u00A0\u00A0\u00A0  ${text}  \u00A0\u00A0\u00A0  ${text}  \u00A0\u00A0\u00A0  ${text}  \u00A0\u00A0\u00A0  `;

  return (
    <div
      data-testid="scrolling-marquee"
      style={{
        backgroundColor: bgColor,
        paddingTop: `${spacingTop}px`,
        paddingBottom: `${spacingBottom}px`,
        overflow: 'hidden',
      }}
    >
      <div
        className="py-4 md:py-5 overflow-hidden"
      >
        <div
          className="marquee-track running"
          style={{
            '--marquee-duration': `${speed}s`,
            color: textColor,
            fontSize: `${fontSize}px`,
            fontFamily: "'Cinzel', Georgia, serif",
            letterSpacing: '0.12em',
            whiteSpace: 'nowrap',
            ...glowStyle,
          }}
        >
          <span>{repeated}</span>
          <span>{repeated}</span>
        </div>
      </div>
    </div>
  );
};

  const renderSection = (section, index) => {
    const content = section.content || {};
    const key = section.id || index;

    switch (section.section_type) {
      case 'hero':
        return (
          <HeroRenderer 
            key={key}
            content={content} 
          />
        );
      case 'cinematic_hero':
        return (
          <section key={key} className="bg-charcoal-900">
            <CinematicHeroMedia heroMedia={heroMedia} />
          </section>
        );
      case 'prophecy':
        return (
          <ProphecySection 
            key={key} 
            content={content}
          />
        );
      case 'coming_soon':
        return (
          <ComingSoonSection 
            key={key} 
            content={content}
          />
        );
      case 'cta':
        return (
          <CTASection 
            key={key} 
            content={content}
          />
        );
      case 'atmosphere':
        return (
          <AtmosphereSection 
            key={key} 
            content={content}
          />
        );
      case 'featured_cards':
        return (
          <FeaturedCardsSection 
            key={key} 
            content={content}
          />
        );
      case 'image_gallery':
        return (
          <GallerySection 
            key={key} 
            content={content}
          />
        );
      case 'slideshow':
        return (
          <SlideshowSection 
            key={key} 
            content={content}
          />
        );
      case 'expandable_narrative':
        return (
          <ExpandableNarrativeSection 
            key={key} 
            content={content}
          />
        );
      case 'how_it_works':
        return (
          <HowItWorksSection 
            key={key} 
            content={content}
          />
        );
      case 'reader_impressions':
        return (
          <ReaderImpressionsSection 
            key={key} 
            content={content}
          />
        );
      case 'first_letter_preview':
        return (
          <FirstLetterPreviewSection 
            key={key} 
            content={content}
          />
        );
      case 'scrolling_marquee':
        return (
          <ScrollingMarqueeBanner
            key={key}
            content={content}
          />
        );
      case 'editorial_story':
        return (
          <EditorialStoryBlock
            key={key}
            content={content}
          />
        );
      case 'feature_spotlight':
        return (
          <FeatureSpotlightSection
            key={key}
            content={content}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-charcoal-900" data-testid="home-page">
      {/* Render all sections in order */}
      {sections.map((section, index) => renderSection(section, index))}

      {/* Free Letter Kit CTA Banner */}
      <section className="py-12 md:py-16 border-t border-gold-500/5">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="font-serif text-gold-400 tracking-[0.3em] text-xs mb-3">FREE DOWNLOAD</p>
          <h2 className="font-display text-2xl md:text-3xl text-parchment-100 mb-3">Get Your Free Vintage Letter Writing Kit</h2>
          <p className="font-serif text-parchment-400 text-sm mb-6 max-w-lg mx-auto">Templates, wax seal guides, and penmanship tips — everything you need to start your own letter-writing tradition.</p>
          <Link
            to="/free-letter-kit"
            data-testid="homepage-freebie-cta"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-gold-600 to-gold-500 hover:from-gold-500 hover:to-gold-400 text-parchment-100 font-display text-sm tracking-widest uppercase rounded transition-all duration-300 shadow-lg hover:shadow-gold-600/20"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Get Your Free Kit
          </Link>
        </div>
      </section>

      {/* Footer */}
      <SiteFooter siteSettings={siteSettings} />
    </div>
  );
};

// ============== CHARACTER CARD COMPONENT ==============
const CharacterCard = ({ character, index }) => {
  const [ref, isVisible] = useScrollReveal(0.2);
  const isEven = index % 2 === 0;

  return (
    <div ref={ref} className={`flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} items-center gap-8 md:gap-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
      <div className="w-full md:w-1/3">
        <div className="relative aspect-square rounded-full overflow-hidden border-2 border-gold-500/30 shadow-2xl">
          {character.portrait_url ? (
            <img src={character.portrait_url} alt={character.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-charcoal-700 flex items-center justify-center">
              <span className="text-6xl text-gold-500/50">?</span>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal-900/50 via-transparent to-transparent" />
        </div>
      </div>
      <div className={`w-full md:w-2/3 ${isEven ? 'md:text-left' : 'md:text-right'}`}>
        <p className="font-serif text-gold-400 italic text-lg mb-4">"{character.short_bio}"</p>
        <h3 className="font-display text-3xl md:text-4xl text-parchment-100 mb-4">{character.name}</h3>
        <p className="font-serif text-parchment-300 leading-relaxed mb-6 line-clamp-4">{character.long_story}</p>
        <Link 
          to="/characters" 
          className="inline-block border border-gold-500/50 hover:border-gold-400 text-gold-400 hover:text-gold-300 font-serif px-6 py-2 rounded transition-colors"
        >
          Read More
        </Link>
      </div>
    </div>
  );
};


// ============== MEMBERSHIP PAGE ==============
const Membership = () => {
  const [heroRef, heroVisible] = useScrollReveal(0.1);
  const navigate = useNavigate();
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [giftForm, setGiftForm] = useState({ email: '', message: '' });
  const [selectedGiftTier, setSelectedGiftTier] = useState(null);
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [showFreeKitBanner, setShowFreeKitBanner] = useState(false);

  const { content: pageContent } = usePageContent('membership');
  const { content: siteSettings } = usePageContent('site_settings');
  const { memberships } = useMemberships();

  // Email capture popup — show after 6 seconds
  useEffect(() => {
    const dismissed = sessionStorage.getItem('freekit_dismissed');
    if (dismissed) return;
    const timer = setTimeout(() => setShowFreeKitBanner(true), 6000);
    return () => clearTimeout(timer);
  }, []);

  // Navigate to character selection with tier info
  const handleSelectTier = (membership, options = {}) => {
    const isPrepaid = membership.is_prepaid || membership.category === 'prepaid' || options.is_prepaid;
    const needsCharacterSelection = membership.character_count > 0 && membership.category !== 'mystery';

    const baseParams = {
      tier_id: membership.id,
      tier_name: membership.name,
      character_count: String(needsCharacterSelection ? (membership.character_count || 1) : 0),
      subscription_type: membership.subscription_type || 'digital',
      price: membership.price,
      billing_cycle: membership.billing_cycle,
      ...(options.is_gift && { is_gift: 'true' }),
      ...(isPrepaid && { is_prepaid: 'true' }),
    };

    if (!needsCharacterSelection) {
      // No character selection needed — go straight to checkout details
      const params = new URLSearchParams({
        ...baseParams,
        selected_characters: '',
      });
      navigate(`/checkout/details?${params.toString()}`);
    } else {
      // Needs character selection
      const params = new URLSearchParams({
        ...baseParams,
        ...(options.gift_recipient_email && { gift_recipient_email: options.gift_recipient_email }),
        ...(options.gift_message && { gift_message: options.gift_message }),
      });
      navigate(`/choose-character?${params.toString()}`);
    }
  };

  const getBillingLabel = (cycle) => {
    switch(cycle) {
      case 'monthly': return '/mo';
      case 'quarterly': return '/qtr';
      case 'yearly': return '/yr';
      case 'one_time': return '';
      default: return '';
    }
  };

  // Layout from page settings
  const pageLayout = pageContent.card_layout || 'grid';

  // Sort memberships by display_order
  const sortedMemberships = [...memberships].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

  // Render a single membership card based on layout
  const renderCard = (plan, index) => {
    const layout = plan.card_layout || 'default';
    const ctaText = plan.cta_text || (plan.character_count === 0 ? 'Purchase Now' : plan.billing_cycle === 'one_time' ? 'Choose Characters' : 'Choose Your Characters');
    const isEditorial = layout === 'image_left' || layout === 'image_right' || layout === 'editorial' || pageLayout === 'editorial';
    const imgLeft = layout === 'image_right' ? false : layout === 'image_left' ? true : index % 2 === 0;

    // Editorial / Side-by-side layout
    if (isEditorial && plan.image_url) {
      return (
        <div key={plan.id} data-testid={`membership-card-${plan.id}`} className="grid md:grid-cols-2 gap-6 md:gap-10 items-center py-8 md:py-12 border-b border-gold-500/10 last:border-b-0">
          {/* Image */}
          <div className={imgLeft ? 'md:order-1' : 'md:order-2'}>
            <div className="overflow-hidden rounded-sm group relative">
              {plan.badge && (
                <span className="absolute top-3 left-3 z-10 font-serif text-[10px] tracking-wider uppercase bg-gold-500/90 text-white px-3 py-1 rounded-sm">{plan.badge}</span>
              )}
              <img src={plan.image_url} alt={plan.image_alt || plan.name} className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.03]" loading="lazy" />
              {plan.image_caption && <p className="font-serif text-parchment-400 text-xs mt-2 italic">{plan.image_caption}</p>}
            </div>
          </div>
          {/* Content */}
          <div className={imgLeft ? 'md:order-2' : 'md:order-1'}>
            {(plan.category_label || plan.category) && <p className="font-serif text-gold-500 tracking-[0.2em] text-[10px] uppercase mb-2">{plan.category_label || plan.category}</p>}
            {plan.subtitle && <p className="font-serif text-parchment-400 text-xs tracking-wide mb-1">{plan.subtitle}</p>}
            <h3 className="font-display text-2xl md:text-3xl text-parchment-100 mb-3">{plan.name}</h3>
            {plan.description_html ? (
              <div className="font-serif text-parchment-300 text-sm leading-[1.8] mb-4 prose max-w-none prose-p:mb-3 prose-p:text-parchment-300 prose-strong:text-parchment-200" dangerouslySetInnerHTML={{ __html: plan.description_html }} />
            ) : plan.description ? (
              <p className="font-serif text-parchment-300 text-sm leading-relaxed mb-4">{plan.description}</p>
            ) : null}
            {plan.features && plan.features.length > 0 && (
              <ul className="space-y-1.5 mb-5">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 font-serif text-parchment-400 text-sm">
                    <svg className="w-4 h-4 text-gold-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    {f}
                  </li>
                ))}
              </ul>
            )}
            <div className="flex items-center gap-4 mb-5">
              {plan.price > 0 ? (
                <div>
                  <span className="font-display text-2xl text-gold-400">${plan.price}</span>
                  <span className="font-serif text-parchment-500 text-sm">{getBillingLabel(plan.billing_cycle)}</span>
                </div>
              ) : (
                <span className="font-serif text-parchment-400 text-sm italic">Price set at checkout</span>
              )}
              {plan.billing_cycle === 'monthly' && <span className="font-serif text-parchment-500 text-xs">Cancel anytime</span>}
            </div>
            <button onClick={() => handleSelectTier(plan)} data-testid={`membership-cta-${plan.id}`} className="font-display text-xs tracking-widest uppercase px-8 py-3 bg-gradient-to-r from-gold-600 to-gold-500 hover:from-gold-500 hover:to-gold-400 text-parchment-100 rounded transition-all duration-300">
              {ctaText}
            </button>
          </div>
        </div>
      );
    }

    // Default grid card
    return (
      <div key={plan.id} data-testid={`membership-card-${plan.id}`} className="bg-charcoal-800/40 border border-gold-500/15 hover:border-gold-500/30 rounded-lg overflow-hidden transition-all duration-300 group flex flex-col">
        {plan.image_url && (
          <div className="aspect-[16/10] overflow-hidden bg-[#F5EFE6] relative">
            {plan.badge && (
              <span className="absolute top-3 left-3 z-10 font-serif text-[10px] tracking-wider uppercase bg-gold-500/90 text-white px-3 py-1 rounded-sm">{plan.badge}</span>
            )}
            {plan.is_featured && !plan.badge && (
              <span className="absolute top-3 left-3 z-10 font-serif text-[10px] tracking-wider uppercase bg-gold-500/90 text-white px-3 py-1 rounded-sm">Most Popular</span>
            )}
            <img src={plan.image_url} alt={plan.image_alt || plan.name} className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-[1.03]" loading="lazy" />
          </div>
        )}
        <div className="p-5 flex flex-col flex-1">
          {(plan.category_label || plan.category) && <p className="font-serif text-gold-500 tracking-[0.15em] text-[10px] uppercase mb-1">{plan.category_label || plan.category}</p>}
          <h3 className="font-display text-lg text-parchment-100 mb-1.5 group-hover:text-gold-400 transition-colors">{plan.name}</h3>
          {plan.subtitle && <p className="font-serif text-parchment-500 text-xs mb-2">{plan.subtitle}</p>}
          <p className="font-serif text-parchment-400 text-sm leading-relaxed mb-3 line-clamp-3 flex-1">{plan.description || ''}</p>
          {plan.features && plan.features.length > 0 && (
            <ul className="space-y-1 mb-4">
              {plan.features.slice(0, 4).map((f, i) => (
                <li key={i} className="flex items-center gap-1.5 font-serif text-parchment-400 text-xs">
                  <svg className="w-3 h-3 text-gold-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  {f}
                </li>
              ))}
            </ul>
          )}
          <div className="mt-auto">
            <div className="flex items-center justify-between mb-4">
              {plan.price > 0 ? (
                <div>
                  <span className="font-display text-xl text-gold-400">${plan.price}</span>
                  <span className="font-serif text-parchment-500 text-sm">{getBillingLabel(plan.billing_cycle)}</span>
                </div>
              ) : (
                <span className="font-serif text-parchment-400 text-sm italic">Price at checkout</span>
              )}
            </div>
            <button onClick={() => handleSelectTier(plan)} data-testid={`membership-cta-${plan.id}`} className="w-full font-display text-xs tracking-widest uppercase py-3 bg-gradient-to-r from-gold-600 to-gold-500 hover:from-gold-500 hover:to-gold-400 text-parchment-100 rounded transition-all duration-300">
              {ctaText}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-charcoal-900 min-h-screen pt-24" data-testid="membership-page">
      {/* Hero Section - Compact */}
      <section ref={heroRef} className="py-8 md:py-12">
        <div 
          className={`max-w-4xl mx-auto px-4 text-center transition-all duration-700 ${
            heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="font-serif text-gold-400 tracking-[0.2em] text-xs mb-2">
            {pageContent.page_label || 'CHOOSE YOUR ADVENTURE'}
          </p>
          <h1 className="font-display text-3xl md:text-5xl text-parchment-100 mb-3">
            {pageContent.title || 'Memberships'}
          </h1>
          <p className="font-serif text-parchment-400 text-base leading-relaxed max-w-xl mx-auto">
            Select a subscription to begin receiving letters from another world.
          </p>
        </div>
      </section>

      {/* Trust Badges */}
      <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8 pb-8 px-4">
        <span className="font-serif text-parchment-400 text-xs md:text-sm flex items-center gap-1.5">
          <svg className="w-4 h-4 text-gold-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          Cancel anytime
        </span>
        <span className="font-serif text-parchment-400 text-xs md:text-sm flex items-center gap-1.5">
          <svg className="w-4 h-4 text-gold-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
          Delivered monthly
        </span>
        <span className="font-serif text-parchment-400 text-xs md:text-sm flex items-center gap-1.5">
          <svg className="w-4 h-4 text-gold-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
          Digital & Physical options
        </span>
      </div>

      {/* Recommendation */}
      <div className="text-center pb-6 px-4">
        <p className="font-serif text-gold-400 text-sm">
          {pageContent.recommendation || 'New here? Start with 1 character — you can always add more later.'}
        </p>
      </div>

      {/* Gift Banner */}
      <div className="max-w-5xl mx-auto px-4 mb-6">
        <Link to="/gift" data-testid="gift-banner" className="flex items-center justify-center gap-3 py-3 px-6 bg-charcoal-800/40 border border-gold-500/15 hover:border-gold-500/30 rounded-lg transition-all duration-300 group">
          <svg className="w-4 h-4 text-gold-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" /></svg>
          <span className="font-serif text-parchment-300 text-sm group-hover:text-gold-400 transition-colors">Sending as a gift? <span className="text-gold-400 underline underline-offset-2">Start here</span></span>
        </Link>
      </div>

      {/* Memberships — Editorial Layout or Grid */}
      <section className="py-6 md:py-10">
        <div className="max-w-5xl mx-auto px-4">
          {pageLayout === 'editorial' ? (
            <div>
              {sortedMemberships.map((plan, i) => renderCard(plan, i))}
            </div>
          ) : (
            <div className={`grid gap-6 ${sortedMemberships.length <= 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
              {sortedMemberships.map((plan, i) => renderCard(plan, i))}
            </div>
          )}
        </div>
      </section>

      {/* Gift Section */}
      <section className="py-16 md:py-24 border-t border-gold-500/5">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="font-serif text-gold-400 tracking-[0.3em] text-xs mb-4">
            {pageContent.gift_label || 'THE GIFT OF STORY'}
          </p>
          <h2 className="font-display text-3xl md:text-4xl text-parchment-100 mb-6">
            {pageContent.gift_title || 'Gift a Membership'}
          </h2>
          <p className="font-serif text-parchment-300 leading-relaxed mb-8">
            {pageContent.gift_description || 'Know someone who would treasure letters from another world? Gift them an invitation to The Timeless Prophecy.'}
          </p>
          <button 
            onClick={() => setShowGiftModal(true)}
            data-testid="gift-membership-btn"
            className="inline-flex items-center gap-3 border border-gold-500/40 hover:border-gold-400 text-parchment-100 hover:text-gold-400 font-serif px-8 py-3 rounded transition-all duration-300"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
            </svg>
            {pageContent.gift_button || 'Give the Gift of Story'}
          </button>
        </div>
      </section>

      {/* Gift Modal */}
      {showGiftModal && (
        <div className="fixed inset-0 bg-charcoal-900/90 z-50 flex items-center justify-center p-4">
          <div className="bg-charcoal-800 border border-gold-500/20 rounded-sm max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-display text-2xl text-parchment-100">Gift a Subscription</h3>
                <button 
                  onClick={() => { setShowGiftModal(false); setSelectedGiftTier(null); setGiftForm({ email: '', message: '' }); }}
                  className="text-parchment-400 hover:text-parchment-100"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {!selectedGiftTier ? (
                <>
                  <p className="font-serif text-parchment-300 mb-6">Choose a subscription tier to gift:</p>
                  <div className="space-y-3">
                    {memberships.filter(m => m.is_giftable !== false).map((tier) => (
                      <button
                        key={tier.id}
                        onClick={() => setSelectedGiftTier(tier)}
                        className="w-full p-4 bg-charcoal-700/50 hover:bg-charcoal-700 border border-gold-500/10 hover:border-gold-500/30 rounded text-left transition-all"
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="font-display text-lg text-parchment-100">{tier.name}</h4>
                            <p className="font-serif text-parchment-400 text-sm">{tier.character_count || 1} character{(tier.character_count || 1) > 1 ? 's' : ''}</p>
                          </div>
                          <span className="font-display text-xl text-gold-400">${tier.price}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-4 p-3 bg-charcoal-700/30 rounded">
                    <p className="font-serif text-parchment-400 text-sm">Selected: <span className="text-gold-400">{selectedGiftTier.name}</span></p>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block font-serif text-parchment-300 text-sm mb-2">Recipient's Email *</label>
                      <input
                        type="email"
                        value={giftForm.email}
                        onChange={(e) => setGiftForm({ ...giftForm, email: e.target.value })}
                        placeholder="friend@email.com"
                        className="w-full bg-charcoal-700 border border-gold-500/20 rounded px-4 py-3 text-parchment-100 font-serif focus:outline-none focus:border-gold-500/50"
                        required
                      />
                    </div>
                    <div>
                      <label className="block font-serif text-parchment-300 text-sm mb-2">Gift Message (optional)</label>
                      <textarea
                        value={giftForm.message}
                        onChange={(e) => setGiftForm({ ...giftForm, message: e.target.value })}
                        placeholder="A personal note to include with the gift..."
                        rows={3}
                        className="w-full bg-charcoal-700 border border-gold-500/20 rounded px-4 py-3 text-parchment-100 font-serif focus:outline-none focus:border-gold-500/50 resize-none"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => setSelectedGiftTier(null)}
                      className="flex-1 font-serif py-3 border border-gold-500/40 text-parchment-100 rounded hover:border-gold-400 transition-colors"
                    >
                      Back
                    </button>
                    <button
                      onClick={() => {
                        if (!giftForm.email) {
                          alert('Please enter recipient email');
                          return;
                        }
                        handleSelectTier(selectedGiftTier, { 
                          is_gift: true, 
                          gift_recipient_email: giftForm.email,
                          gift_message: giftForm.message
                        });
                        setShowGiftModal(false);
                      }}
                      data-testid="gift-continue-btn"
                      className="flex-1 font-serif py-3 bg-gold-500 hover:bg-gold-400 text-parchment-100 rounded transition-colors"
                    >
                      Choose Characters
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <SiteFooter siteSettings={siteSettings} />

      {/* Sticky CTA Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-charcoal-800/95 backdrop-blur-sm border-t border-gold-500/20 py-3 px-4" data-testid="sticky-cta">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          <div className="hidden sm:block">
            <p className="font-serif text-parchment-300 text-sm">Letters from $6.99/mo · Cancel anytime</p>
          </div>
          <Link
            to="/membership"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="inline-block px-8 py-2.5 bg-gradient-to-r from-gold-600 to-gold-500 hover:from-gold-500 hover:to-gold-400 text-parchment-100 font-display text-xs tracking-widest uppercase rounded transition-all duration-300 whitespace-nowrap"
            data-testid="sticky-cta-button"
          >
            Start Your First Letter
          </Link>
        </div>
      </div>

      {/* Free Kit Popup Banner */}
      {showFreeKitBanner && (
        <div className="fixed bottom-16 left-4 right-4 md:left-auto md:right-6 md:max-w-sm z-50 bg-charcoal-800 border border-gold-500/30 rounded-lg shadow-2xl p-5 animate-fade-in-slow" data-testid="freekit-popup">
          <button
            onClick={() => { setShowFreeKitBanner(false); sessionStorage.setItem('freekit_dismissed', '1'); }}
            className="absolute top-2 right-2 text-parchment-400 hover:text-parchment-100"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
          <p className="font-display text-parchment-100 text-base mb-1">Free Letter Kit</p>
          <p className="font-serif text-parchment-400 text-sm mb-3">Get a free sample letter and activity kit delivered to your inbox.</p>
          <Link
            to="/free-letter-kit"
            onClick={() => { setShowFreeKitBanner(false); sessionStorage.setItem('freekit_dismissed', '1'); }}
            className="inline-block px-6 py-2 bg-gradient-to-r from-gold-600 to-gold-500 text-parchment-100 font-display text-xs tracking-widest uppercase rounded transition-all"
          >
            Get Your Free Kit
          </Link>
        </div>
      )}

    </div>
  );
};

// ============== CHARACTER SELECTION PAGE ==============
const ChooseCharacter = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedCharacters, setSelectedCharacters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tierData, setTierData] = useState(null);

  const { characters } = useCharacters();
  const { content: siteSettings } = usePageContent('site_settings');

  // Get tier info from URL params
  const tierId = searchParams.get('tier_id');
  const tierName = searchParams.get('tier_name') || 'Subscription';
  const characterCount = parseInt(searchParams.get('character_count') || '1', 10);
  const subscriptionType = searchParams.get('subscription_type') || 'digital';
  const price = searchParams.get('price') || '0';
  const billingCycle = searchParams.get('billing_cycle') || 'monthly';
  const isGift = searchParams.get('is_gift') === 'true';
  const isPrepaid = searchParams.get('is_prepaid') === 'true';
  const giftRecipientEmail = searchParams.get('gift_recipient_email') || '';
  const giftMessage = searchParams.get('gift_message') || '';

  // Fetch tier data to get assigned character_ids
  useEffect(() => {
    if (!tierId) return;
    const fetchTier = async () => {
      try {
        const response = await fetch(`${API}/memberships/${tierId}`);
        if (response.ok) {
          const data = await response.json();
          setTierData(data);
        }
      } catch (err) {
        console.error('Error fetching tier data:', err);
      }
    };
    fetchTier();
  }, [tierId]);

  // Filter characters: show only those assigned to this tier, or all if none assigned
  const assignedCharacterIds = tierData?.character_ids || [];
  const selectableCharacters = characters.filter(c => {
    if (c.status !== 'active' || !c.slug) return false;
    if (c.slug.toLowerCase() === 'tommy') return false;
    // If the tier has specific character assignments, only show those
    if (assignedCharacterIds.length > 0) {
      return assignedCharacterIds.includes(c.id);
    }
    return true;
  });

  // Redirect if missing required params or if character count is 0 (shouldn't be on this page)
  useEffect(() => {
    if (!tierId) {
      navigate('/membership');
    } else if (characterCount === 0) {
      const params = new URLSearchParams({
        tier_id: tierId, tier_name: tierName, character_count: '0',
        subscription_type: subscriptionType, price, billing_cycle: billingCycle,
        is_gift: isGift.toString(), is_prepaid: isPrepaid.toString(), selected_characters: '',
      });
      navigate(`/checkout/details?${params.toString()}`, { replace: true });
    }
  }, [tierId, characterCount, navigate, tierName, subscriptionType, price, billingCycle, isGift, isPrepaid]);

  const toggleCharacter = (slug) => {
    if (selectedCharacters.includes(slug)) {
      setSelectedCharacters(selectedCharacters.filter(s => s !== slug));
    } else {
      if (selectedCharacters.length < characterCount) {
        setSelectedCharacters([...selectedCharacters, slug]);
      }
    }
  };

  const handleProceedToCheckout = async () => {
    if (selectedCharacters.length !== characterCount) {
      setError(`Please select exactly ${characterCount} character${characterCount > 1 ? 's' : ''}`);
      return;
    }

    // Navigate to pre-checkout page with all necessary data
    const params = new URLSearchParams({
      tier_id: tierId,
      tier_name: tierName,
      character_count: characterCount.toString(),
      subscription_type: subscriptionType,
      price: price,
      billing_cycle: billingCycle,
      is_gift: isGift.toString(),
      is_prepaid: isPrepaid.toString(),
      selected_characters: selectedCharacters.join(','),
    });
    
    navigate(`/checkout/details?${params.toString()}`);
  };

  const getTypeLabel = () => {
    if (isGift) return 'Gift Subscription';
    if (isPrepaid) return 'Prepaid Subscription';
    return subscriptionType === 'physical' ? 'Physical Letters' : 'Digital Letters';
  };

  const getBillingLabel = () => {
    switch(billingCycle) {
      case 'monthly': return '/month';
      case 'quarterly': return '/quarter';
      case 'yearly': return '/year';
      default: return '';
    }
  };

  return (
    <div className="bg-charcoal-900 min-h-screen pt-24" data-testid="choose-character-page">
      {/* Header */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="font-serif text-gold-400 tracking-[0.3em] text-xs mb-4">
            {isGift ? 'GIFT SUBSCRIPTION' : 'CHOOSE YOUR CORRESPONDENTS'}
          </p>
          <h1 className="font-display text-3xl md:text-5xl text-parchment-100 mb-4">
            Select Your Character{characterCount > 1 ? 's' : ''}
          </h1>
          <p className="font-serif text-parchment-300 text-lg leading-relaxed max-w-xl mx-auto">
            {characterCount === 1 
              ? 'Choose the character whose letters you wish to receive.'
              : `Choose ${characterCount} characters whose letters you wish to receive.`
            }
          </p>
        </div>
      </section>

      {/* Tier Summary */}
      <section className="pb-8">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-charcoal-800/50 border border-gold-500/20 rounded-sm p-4 flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="font-serif text-parchment-400 text-sm">{getTypeLabel()}</p>
              <h3 className="font-display text-xl text-parchment-100">{tierName}</h3>
            </div>
            <div className="text-right">
              <span className="font-display text-2xl text-gold-400">${price}</span>
              <span className="font-serif text-parchment-400">{getBillingLabel()}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Selection Counter */}
      <section className="pb-6">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-center gap-4">
            <div className="flex items-center gap-2">
              {Array.from({ length: characterCount }).map((_, i) => (
                <div
                  key={i}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    i < selectedCharacters.length ? 'bg-gold-400' : 'bg-charcoal-700 border border-gold-500/30'
                  }`}
                />
              ))}
            </div>
            <p className="font-serif text-parchment-300">
              Selected: <span className="text-gold-400 font-display">{selectedCharacters.length}</span> of <span className="text-parchment-100">{characterCount}</span>
            </p>
          </div>
        </div>
      </section>

      {/* Character Cards */}
      <section className="py-8">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {selectableCharacters.map((character) => {
              const isSelected = selectedCharacters.includes(character.slug);
              const isDisabled = !isSelected && selectedCharacters.length >= characterCount;

              return (
                <button
                  key={character.id}
                  onClick={() => toggleCharacter(character.slug)}
                  disabled={isDisabled}
                  data-testid={`character-select-${character.slug}`}
                  className={`group relative text-left rounded-sm overflow-hidden transition-all duration-300 ${
                    isSelected 
                      ? 'ring-2 ring-gold-400 ring-offset-2 ring-offset-charcoal-900' 
                      : isDisabled
                        ? 'opacity-40 cursor-not-allowed'
                        : 'hover:ring-1 hover:ring-gold-500/50'
                  }`}
                >
                  {/* Selection indicator */}
                  {isSelected && (
                    <div className="absolute top-4 right-4 z-10 w-8 h-8 bg-gold-400 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-parchment-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}

                  {/* Portrait */}
                  <div className="aspect-[3/4] overflow-hidden bg-charcoal-800">
                    {character.portrait_url ? (
                      <img
                        src={character.portrait_url}
                        alt={character.name}
                        className={`w-full h-full object-cover transition-all duration-500 ${
                          isSelected ? 'scale-105' : 'group-hover:scale-105'
                        }`}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-gold-400/30 text-6xl">?</span>
                      </div>
                    )}
                  </div>

                  {/* Card Content */}
                  <div className={`p-5 transition-colors ${isSelected ? 'bg-gold-500/10' : 'bg-charcoal-800/70'}`}>
                    {character.era && (
                      <p className="font-serif text-gold-400/70 tracking-[0.15em] text-xs mb-1 uppercase">
                        {character.era}
                      </p>
                    )}
                    <h3 className="font-display text-xl text-parchment-100 mb-2">
                      {character.name}
                    </h3>
                    <p className="font-serif text-parchment-400 text-sm line-clamp-2">
                      {character.short_bio || 'A mysterious correspondent awaits...'}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          {selectableCharacters.length === 0 && (
            <div className="text-center py-12">
              <p className="font-serif text-parchment-400">No characters available for selection.</p>
              <Link to="/membership" className="font-serif text-gold-400 hover:text-gold-300 mt-4 inline-block">
                ← Back to Membership
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Error Message */}
      {error && (
        <div className="max-w-4xl mx-auto px-6 pb-4">
          <div className="bg-red-900/30 border border-red-500/30 rounded-sm p-4 text-center">
            <p className="font-serif text-red-300">{error}</p>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-12 border-t border-gold-500/5">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <Link 
              to="/membership" 
              className="font-serif text-parchment-400 hover:text-parchment-100 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Plans
            </Link>

            <button
              onClick={handleProceedToCheckout}
              disabled={loading || selectedCharacters.length !== characterCount}
              data-testid="checkout-btn"
              className={`inline-flex items-center justify-center gap-3 font-serif text-base px-10 py-4 rounded-sm transition-all duration-300 ${
                selectedCharacters.length === characterCount
                  ? 'bg-gold-500 hover:bg-gold-400 text-parchment-100'
                  : 'bg-charcoal-700 text-parchment-500 cursor-not-allowed'
              }`}
            >
              {loading ? (
                <>
                  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  Continue to Secure Checkout
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </>
              )}
            </button>
          </div>
        </div>
      </section>

      <SiteFooter siteSettings={siteSettings} />
    </div>
  );
};

// ============== PRE-CHECKOUT PAGE: BEGIN YOUR CORRESPONDENCE ==============
const PreCheckout = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { content: siteSettings } = usePageContent('site_settings');
  const { characters } = useCharacters();

  // Gift preview modal state
  const [showPreview, setShowPreview] = useState(false);

  // Get data from URL params
  const tierId = searchParams.get('tier_id');
  const tierName = searchParams.get('tier_name') || 'Subscription';
  const subscriptionType = searchParams.get('subscription_type') || 'digital';
  const price = searchParams.get('price') || '0';
  const billingCycle = searchParams.get('billing_cycle') || 'monthly';
  const isGift = searchParams.get('is_gift') === 'true';
  const isPrepaid = searchParams.get('is_prepaid') === 'true';
  const selectedCharacters = searchParams.get('selected_characters')?.split(',').filter(Boolean) || [];

  // Resolve character names from slugs
  const selectedCharacterNames = selectedCharacters
    .map(slug => characters.find(c => c.slug === slug)?.name)
    .filter(Boolean);

  // Form state - Personal subscription
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [preferredName, setPreferredName] = useState('');
  const [email, setEmail] = useState('');

  // Form state - Gift subscription
  const [recipientFirstName, setRecipientFirstName] = useState('');
  const [recipientLastName, setRecipientLastName] = useState('');
  const [recipientPreferredName, setRecipientPreferredName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientPostalAddress, setRecipientPostalAddress] = useState('');
  const [giftMessage, setGiftMessage] = useState('');
  const [senderName, setSenderName] = useState('');

  // Gift delivery options
  const [giftDeliveryType, setGiftDeliveryType] = useState('immediate');
  const [giftDeliveryDate, setGiftDeliveryDate] = useState('');

  // Redirect if missing required params
  useEffect(() => {
    if (!tierId) {
      navigate('/membership');
    }
  }, [tierId, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validation
    if (!isGift) {
      if (!firstName.trim() || !lastName.trim() || !email.trim()) {
        setError('Please fill in all required fields');
        setLoading(false);
        return;
      }
    } else {
      if (!firstName.trim() || !lastName.trim() || !email.trim()) {
        setError('Please fill in your details');
        setLoading(false);
        return;
      }
      if (!recipientFirstName.trim() || !recipientLastName.trim()) {
        setError('Please fill in recipient name');
        setLoading(false);
        return;
      }
      if (subscriptionType === 'digital' && !recipientEmail.trim()) {
        setError('Please provide recipient email for digital delivery');
        setLoading(false);
        return;
      }
      if (subscriptionType === 'physical' && !recipientPostalAddress.trim()) {
        setError('Please provide recipient postal address for physical delivery');
        setLoading(false);
        return;
      }
    }

    try {
      const checkoutData = {
        membership_tier_id: tierId,
        selected_character_slugs: selectedCharacters,
        origin_url: window.location.origin,
        is_gift: isGift,
        is_prepaid: isPrepaid,
        // Personal/buyer info
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        preferred_name: preferredName.trim() || null,
        email: email.trim(),
      };

      if (isGift) {
        checkoutData.recipient_first_name = recipientFirstName.trim();
        checkoutData.recipient_last_name = recipientLastName.trim();
        checkoutData.recipient_preferred_name = recipientPreferredName.trim() || null;
        checkoutData.recipient_email = recipientEmail.trim() || null;
        checkoutData.recipient_postal_address = recipientPostalAddress.trim() || null;
        checkoutData.gift_message = giftMessage.trim() || null;
        checkoutData.sender_name = senderName.trim() || `${firstName} ${lastName}`.trim();
        checkoutData.gift_delivery_type = giftDeliveryType;
        checkoutData.gift_delivery_date = giftDeliveryType === 'scheduled' ? giftDeliveryDate : null;
      }

      const response = await fetch(`${API}/checkout/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(checkoutData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Checkout failed');
      }

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message || 'Unable to start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getBillingLabel = () => {
    if (billingCycle === 'one_time') return 'one-time';
    if (billingCycle === 'yearly') return '/year';
    return '/month';
  };

  return (
    <div className="bg-charcoal-900 min-h-screen pt-24" data-testid="pre-checkout-page">
      {/* Victorian Header */}
      <section className="py-12 md:py-16">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <p className="font-serif text-gold-400 tracking-[0.3em] text-xs mb-4">
            {isGift ? 'A GIFT OF STORIES' : 'YOUR JOURNEY AWAITS'}
          </p>
          <h1 className="font-display text-3xl md:text-5xl text-parchment-100 mb-4">
            Begin Your Correspondence
          </h1>
          <p className="font-serif text-parchment-400 text-lg">
            {isGift 
              ? 'Share the magic of handwritten letters with someone special'
              : 'Enter your details to receive your letters'
            }
          </p>
        </div>
      </section>

      {/* Order Summary */}
      <section className="pb-8">
        <div className="max-w-xl mx-auto px-6">
          <div className="bg-charcoal-800/50 border border-gold-900/30 rounded-lg p-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-display text-parchment-100 text-lg">{tierName}</p>
                <p className="font-serif text-parchment-400 text-sm">
                  {subscriptionType === 'physical' ? 'Physical Letters' : 'Digital Letters'}
                  {selectedCharacters.length > 0 && ` • ${selectedCharacters.length} character${selectedCharacters.length > 1 ? 's' : ''}`}
                </p>
              </div>
              <div className="text-right">
                <p className="font-display text-gold-400 text-xl">${price}</p>
                <p className="font-serif text-parchment-500 text-sm">{getBillingLabel()}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Victorian Form */}
      <section className="pb-24">
        <div className="max-w-xl mx-auto px-6">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Parchment-styled form container */}
            <div 
              className="rounded-lg p-8 md:p-10"
              style={{
                background: 'linear-gradient(145deg, #f4efe4 0%, #e8e0d0 50%, #ddd5c5 100%)',
                boxShadow: '0 4px 20px rgba(176,144,96,0.08), inset 0 1px 0 rgba(255,255,255,0.8)'
              }}
            >
              {/* Your Details Section */}
              <div className="mb-8">
                <h2 className="font-display text-parchment-100 text-xl mb-1">
                  {isGift ? 'Your Details' : 'Subscriber Details'}
                </h2>
                <p className="font-serif text-parchment-300 text-sm mb-6">
                  {isGift ? 'As the gift giver' : 'Where shall we send your letters?'}
                </p>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block font-serif text-parchment-200 text-sm mb-1">First Name *</label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full px-4 py-3 bg-white/80 border border-parchment-500 rounded font-serif text-parchment-100 placeholder-parchment-500 focus:outline-none focus:border-gold-600 focus:ring-1 focus:ring-gold-600"
                      placeholder="Eleanor"
                      required
                    />
                  </div>
                  <div>
                    <label className="block font-serif text-parchment-200 text-sm mb-1">Last Name *</label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full px-4 py-3 bg-white/80 border border-parchment-500 rounded font-serif text-parchment-100 placeholder-parchment-500 focus:outline-none focus:border-gold-600 focus:ring-1 focus:ring-gold-600"
                      placeholder="Worthington"
                      required
                    />
                  </div>
                </div>
                
                <div className="mb-4">
                  <label className="block font-serif text-parchment-200 text-sm mb-1">
                    Preferred Name <span className="text-parchment-500">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={preferredName}
                    onChange={(e) => setPreferredName(e.target.value)}
                    className="w-full px-4 py-3 bg-white/80 border border-parchment-500 rounded font-serif text-parchment-100 placeholder-parchment-500 focus:outline-none focus:border-gold-600 focus:ring-1 focus:ring-gold-600"
                    placeholder="Ellie"
                  />
                  <p className="font-serif text-parchment-400 text-xs mt-1">How shall the characters address you?</p>
                </div>
                
                <div>
                  <label className="block font-serif text-parchment-200 text-sm mb-1">Email Address *</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 bg-white/80 border border-parchment-500 rounded font-serif text-parchment-100 placeholder-parchment-500 focus:outline-none focus:border-gold-600 focus:ring-1 focus:ring-gold-600"
                    placeholder="eleanor@example.com"
                    required
                  />
                </div>
              </div>

              {/* Gift Recipient Section */}
              {isGift && (
                <>
                  <div className="border-t border-parchment-500/50 my-8"></div>
                  
                  <div className="mb-8">
                    <h2 className="font-display text-parchment-100 text-xl mb-1">Gift Recipient</h2>
                    <p className="font-serif text-parchment-300 text-sm mb-6">Who shall receive this gift?</p>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block font-serif text-parchment-200 text-sm mb-1">First Name *</label>
                        <input
                          type="text"
                          value={recipientFirstName}
                          onChange={(e) => setRecipientFirstName(e.target.value)}
                          className="w-full px-4 py-3 bg-white/80 border border-parchment-500 rounded font-serif text-parchment-100 placeholder-parchment-500 focus:outline-none focus:border-gold-600 focus:ring-1 focus:ring-gold-600"
                          required
                        />
                      </div>
                      <div>
                        <label className="block font-serif text-parchment-200 text-sm mb-1">Last Name *</label>
                        <input
                          type="text"
                          value={recipientLastName}
                          onChange={(e) => setRecipientLastName(e.target.value)}
                          className="w-full px-4 py-3 bg-white/80 border border-parchment-500 rounded font-serif text-parchment-100 placeholder-parchment-500 focus:outline-none focus:border-gold-600 focus:ring-1 focus:ring-gold-600"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block font-serif text-parchment-200 text-sm mb-1">
                        Preferred Name <span className="text-parchment-500">(optional)</span>
                      </label>
                      <input
                        type="text"
                        value={recipientPreferredName}
                        onChange={(e) => setRecipientPreferredName(e.target.value)}
                        className="w-full px-4 py-3 bg-white/80 border border-parchment-500 rounded font-serif text-parchment-100 placeholder-parchment-500 focus:outline-none focus:border-gold-600 focus:ring-1 focus:ring-gold-600"
                      />
                    </div>
                    
                    {subscriptionType === 'digital' ? (
                      <div>
                        <label className="block font-serif text-parchment-200 text-sm mb-1">Recipient Email *</label>
                        <input
                          type="email"
                          value={recipientEmail}
                          onChange={(e) => setRecipientEmail(e.target.value)}
                          className="w-full px-4 py-3 bg-white/80 border border-parchment-500 rounded font-serif text-parchment-100 placeholder-parchment-500 focus:outline-none focus:border-gold-600 focus:ring-1 focus:ring-gold-600"
                          required
                        />
                      </div>
                    ) : (
                      <div>
                        <label className="block font-serif text-parchment-200 text-sm mb-1">Recipient Postal Address *</label>
                        <textarea
                          value={recipientPostalAddress}
                          onChange={(e) => setRecipientPostalAddress(e.target.value)}
                          rows={3}
                          className="w-full px-4 py-3 bg-white/80 border border-parchment-500 rounded font-serif text-parchment-100 placeholder-parchment-500 focus:outline-none focus:border-gold-600 focus:ring-1 focus:ring-gold-600 resize-none"
                          placeholder="123 Victorian Lane&#10;London, SW1A 1AA&#10;United Kingdom"
                          required
                        />
                      </div>
                    )}
                  </div>

                  {/* Gift Message */}
                  <div className="mb-8">
                    <label className="block font-serif text-parchment-200 text-sm mb-1">
                      Gift Message <span className="text-parchment-500">(optional)</span>
                    </label>
                    <textarea
                      value={giftMessage}
                      onChange={(e) => setGiftMessage(e.target.value)}
                      rows={3}
                      className="w-full px-4 py-3 bg-white/80 border border-parchment-500 rounded font-serif text-parchment-100 placeholder-parchment-500 focus:outline-none focus:border-gold-600 focus:ring-1 focus:ring-gold-600 resize-none"
                      placeholder="A personal note to accompany your gift..."
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block font-serif text-parchment-200 text-sm mb-1">
                      Your Name (as sender) <span className="text-parchment-500">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={senderName}
                      onChange={(e) => setSenderName(e.target.value)}
                      className="w-full px-4 py-3 bg-white/80 border border-parchment-500 rounded font-serif text-parchment-100 placeholder-parchment-500 focus:outline-none focus:border-gold-600 focus:ring-1 focus:ring-gold-600"
                      placeholder={`${firstName} ${lastName}`.trim() || 'Your name'}
                    />
                  </div>

                  {/* Gift Delivery Options */}
                  <div className="border-t border-parchment-500/50 my-8"></div>
                  
                  <div>
                    <h2 className="font-display text-parchment-100 text-xl mb-1">Delivery Options</h2>
                    <p className="font-serif text-parchment-300 text-sm mb-6">When shall the recipient receive notification?</p>
                    
                    <div className="space-y-3">
                      <label className="flex items-start gap-3 p-4 bg-white/50 border border-parchment-500 rounded cursor-pointer hover:border-gold-600 transition-colors">
                        <input
                          type="radio"
                          name="deliveryType"
                          value="immediate"
                          checked={giftDeliveryType === 'immediate'}
                          onChange={(e) => setGiftDeliveryType(e.target.value)}
                          className="mt-1 w-4 h-4 text-gold-600 border-parchment-500 focus:ring-gold-500"
                        />
                        <div>
                          <span className="font-serif text-parchment-100 font-medium">Immediate Delivery</span>
                          <p className="font-serif text-parchment-400 text-sm">Recipient will be notified right away</p>
                        </div>
                      </label>
                      
                      <label className="flex items-start gap-3 p-4 bg-white/50 border border-parchment-500 rounded cursor-pointer hover:border-gold-600 transition-colors">
                        <input
                          type="radio"
                          name="deliveryType"
                          value="scheduled"
                          checked={giftDeliveryType === 'scheduled'}
                          onChange={(e) => setGiftDeliveryType(e.target.value)}
                          className="mt-1 w-4 h-4 text-gold-600 border-parchment-500 focus:ring-gold-500"
                        />
                        <div className="flex-1">
                          <span className="font-serif text-parchment-100 font-medium">Scheduled Delivery</span>
                          <p className="font-serif text-parchment-400 text-sm mb-2">Choose a special date</p>
                          {giftDeliveryType === 'scheduled' && (
                            <input
                              type="date"
                              value={giftDeliveryDate}
                              onChange={(e) => setGiftDeliveryDate(e.target.value)}
                              min={new Date().toISOString().split('T')[0]}
                              className="w-full px-3 py-2 bg-white border border-parchment-500 rounded font-serif text-parchment-100 focus:outline-none focus:border-gold-600"
                              required={giftDeliveryType === 'scheduled'}
                            />
                          )}
                        </div>
                      </label>
                      
                      <label className="flex items-start gap-3 p-4 bg-white/50 border border-parchment-500 rounded cursor-pointer hover:border-gold-600 transition-colors">
                        <input
                          type="radio"
                          name="deliveryType"
                          value="silent"
                          checked={giftDeliveryType === 'silent'}
                          onChange={(e) => setGiftDeliveryType(e.target.value)}
                          className="mt-1 w-4 h-4 text-gold-600 border-parchment-500 focus:ring-gold-500"
                        />
                        <div>
                          <span className="font-serif text-parchment-100 font-medium">Silent Gift</span>
                          <p className="font-serif text-parchment-400 text-sm">No notification—reveal the gift yourself</p>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Gift Preview Link */}
                  <div className="border-t border-parchment-500/50 my-8"></div>
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => setShowPreview(true)}
                      className="inline-flex items-center gap-2 font-serif text-gold-700 hover:text-gold-600 transition-colors"
                      data-testid="preview-correspondence-btn"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Preview The First Correspondence
                    </button>
                    <p className="font-serif text-parchment-400 text-xs mt-1">See what your recipient will receive</p>
                  </div>
                </>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-4 text-center">
                <p className="font-serif text-red-400">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full px-8 py-4 bg-gold-600 hover:bg-gold-500 disabled:bg-charcoal-700 text-parchment-100 disabled:text-parchment-400 font-display text-lg rounded transition-all duration-300 flex items-center justify-center gap-3"
              data-testid="proceed-to-payment-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Processing...
                </span>
              ) : (
                <>
                  Proceed to Payment
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </>
              )}
            </button>

            {/* Security Note */}
            <p className="text-center font-serif text-parchment-500 text-sm">
              <svg className="w-4 h-4 inline-block mr-1 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Secure payment powered by Stripe
            </p>
          </form>
        </div>
      </section>

      <SiteFooter siteSettings={siteSettings} />

      {/* Gift Preview Modal */}
      {showPreview && isGift && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal-900/80 backdrop-blur-sm"
          onClick={() => setShowPreview(false)}
        >
          <div 
            className="relative max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'linear-gradient(145deg, #f4efe4 0%, #e8e0d0 50%, #ddd5c5 100%)',
              boxShadow: '0 15px 40px rgba(176,144,96,0.1), inset 0 1px 0 rgba(255,255,255,0.8)'
            }}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowPreview(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-charcoal-200/50 hover:bg-charcoal-300/50 text-parchment-300 hover:text-parchment-100 transition-colors"
              data-testid="close-preview-btn"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Decorative Header */}
            <div className="bg-charcoal-900 px-8 py-6 text-center">
              <p className="font-serif text-gold-400 tracking-[0.3em] text-xs mb-2">PREVIEW</p>
              <h2 className="font-display text-2xl text-parchment-100">The First Correspondence</h2>
            </div>

            {/* Email Content Preview */}
            <div className="p-8 md:p-10">
              {/* Recipient Greeting */}
              <p className="font-serif text-parchment-100 text-lg mb-6">
                Dear {recipientPreferredName || recipientFirstName || 'Reader'},
              </p>

              {/* Main Message */}
              <p className="font-serif text-parchment-200 mb-4 leading-relaxed">
                A gift has arrived for you—one wrapped not in paper, but in mystery and ink.
              </p>

              <p className="font-serif text-parchment-200 mb-4 leading-relaxed">
                {(senderName || `${firstName} ${lastName}`.trim() || 'Someone who cares for you')} has gifted you{' '}
                <span className="font-semibold">{tierName}</span>
                {selectedCharacterNames.length > 0 && (
                  <>, a journey with{' '}
                    <span className="font-semibold text-parchment-100">
                      {selectedCharacterNames.length === 1 
                        ? selectedCharacterNames[0]
                        : selectedCharacterNames.length === 2
                          ? `${selectedCharacterNames[0]} and ${selectedCharacterNames[1]}`
                          : `${selectedCharacterNames.slice(0, -1).join(', ')} and ${selectedCharacterNames[selectedCharacterNames.length - 1]}`
                      }
                    </span>
                  </>
                )}.
              </p>

              {/* Character Journey Line */}
              <div className="my-6 py-4 px-6 bg-white/50 border-l-4 border-gold-600 rounded-r">
                <p className="font-serif text-parchment-100 italic">
                  {selectedCharacterNames.length === 1 
                    ? `Your journey with ${selectedCharacterNames[0]} begins now.`
                    : selectedCharacterNames.length > 1
                      ? `Your journeys with ${selectedCharacterNames.length === 2 
                          ? `${selectedCharacterNames[0]} and ${selectedCharacterNames[1]}`
                          : `${selectedCharacterNames.slice(0, -1).join(', ')} and ${selectedCharacterNames[selectedCharacterNames.length - 1]}`
                        } begin now.`
                      : 'Your journey begins now.'
                  }
                </p>
              </div>

              {/* Gift Message */}
              {giftMessage && (
                <div className="my-6 p-6 bg-charcoal-100/30 rounded-lg border border-parchment-500/50">
                  <p className="font-serif text-parchment-400 text-sm mb-2">A message from the sender:</p>
                  <p className="font-serif text-parchment-100 italic leading-relaxed">"{giftMessage}"</p>
                  <p className="font-serif text-parchment-300 text-sm mt-3 text-right">
                    — {senderName || `${firstName} ${lastName}`.trim() || 'Your gifter'}
                  </p>
                </div>
              )}

              {/* Delivery Type Notice */}
              <div className="mt-6 pt-6 border-t border-parchment-500/50">
                <p className="font-serif text-parchment-300 text-sm flex items-center gap-2">
                  <svg className="w-4 h-4 text-gold-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {giftDeliveryType === 'immediate' && 'This email will be sent immediately after purchase.'}
                  {giftDeliveryType === 'scheduled' && giftDeliveryDate && `This email will be sent on ${new Date(giftDeliveryDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}.`}
                  {giftDeliveryType === 'silent' && 'This email will not be sent. You will reveal the gift yourself.'}
                </p>
              </div>

              {/* Subscription Type */}
              <p className="font-serif text-parchment-400 text-sm mt-4">
                {subscriptionType === 'physical' 
                  ? 'Handwritten letters will be mailed to the postal address provided.'
                  : 'Digital letters will arrive via email.'
                }
              </p>

              {/* Closing */}
              <div className="mt-8 pt-6 border-t border-parchment-500/50 text-center">
                <p className="font-serif text-parchment-300 italic text-sm">
                  "When ink meets parchment under the light of believing hearts,<br />stories become doorways..."
                </p>
                <p className="font-display text-parchment-200 text-lg mt-4">The Timeless Post</p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-8 pb-8 text-center">
              <button
                onClick={() => setShowPreview(false)}
                className="px-8 py-3 bg-charcoal-900 hover:bg-charcoal-800 text-parchment-100 font-serif rounded transition-colors"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== CHARACTERS INDEX PAGE ==============
const Characters = () => {
  const [heroRef, heroVisible] = useScrollReveal(0.1);
  const [gridRef, gridVisible] = useScrollReveal(0.1);

  const { content: pageContent } = usePageContent('characters');
  const { content: siteSettings } = usePageContent('site_settings');
  const { characters } = useCharacters();

  const activeCharacters = characters.filter(c => c.status === 'active');
  const comingSoonCharacters = characters.filter(c => c.status === 'coming_soon');

  return (
    <div className="bg-charcoal-900 min-h-screen pt-24" data-testid="characters-page">
      <section ref={heroRef} className="py-16 md:py-24">
        <div 
          className={`max-w-4xl mx-auto px-6 text-center transition-all duration-1000 ${
            heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="font-serif text-gold-400 tracking-[0.3em] text-xs mb-4">
            {pageContent.page_label || 'THE VOICES OF THE PROPHECY'}
          </p>
          <h1 className="font-display text-4xl md:text-6xl text-parchment-100 mb-6">
            {pageContent.title || 'Meet the Characters'}
          </h1>
          <p className="font-serif text-parchment-300 text-lg leading-relaxed max-w-2xl mx-auto">
            {pageContent.description || 'Each character exists in their own corner of the story.'}
          </p>
        </div>
      </section>

      {/* Character Cards Grid */}
      <section ref={gridRef} className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {activeCharacters.map((character, index) => (
              <Link
                key={character.id}
                to={character.slug ? `/characters/${character.slug}` : '#'}
                data-testid={`character-card-${index}`}
                className={`group block bg-charcoal-800/50 border border-gold-500/10 hover:border-gold-500/30 rounded-sm overflow-hidden transition-all duration-700 ${
                  gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                {/* Portrait */}
                <div className="aspect-[3/4] overflow-hidden">
                  {character.portrait_url ? (
                    <img
                      src={character.portrait_url}
                      alt={character.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full bg-charcoal-800 flex items-center justify-center">
                      <span className="text-gold-400/30 text-6xl">?</span>
                    </div>
                  )}
                </div>
                {/* Card Content */}
                <div className="p-6">
                  {character.era && (
                    <p className="font-serif text-gold-400/70 tracking-[0.2em] text-xs mb-2 uppercase">
                      {character.era}
                    </p>
                  )}
                  <h3 className="font-display text-2xl text-parchment-100 mb-3 group-hover:text-gold-400 transition-colors">
                    {character.name}
                  </h3>
                  <p className="font-serif text-parchment-400 text-sm leading-relaxed line-clamp-3">
                    {character.short_bio || 'A mysterious figure awaiting discovery...'}
                  </p>
                  <div className="mt-4 flex items-center gap-2 font-serif text-gold-400 text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>Discover their story</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Coming Soon Section */}
      {comingSoonCharacters.length > 0 && (
        <section className="py-24 border-t border-gold-500/5">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <p className="font-serif text-gold-400 tracking-[0.3em] text-xs mb-4">
              {pageContent.coming_soon_label || 'MORE VOICES AWAKENING'}
            </p>
            <h2 className="font-display text-3xl md:text-4xl text-parchment-100 mb-12">
              {pageContent.coming_soon_title || 'Coming Soon'}
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {comingSoonCharacters.map((character) => (
                <div 
                  key={character.id}
                  className="p-6 border border-gold-500/20 rounded-sm bg-charcoal-800/30"
                >
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-charcoal-700 flex items-center justify-center">
                    <span className="font-serif text-gold-400 text-2xl">?</span>
                  </div>
                  <h3 className="font-display text-xl text-parchment-100 mb-2">{character.name}</h3>
                  <p className="font-serif text-parchment-400 text-sm italic">{character.short_bio}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <SiteFooter siteSettings={siteSettings} />
    </div>
  );
};

// ============== SINGLE CHARACTER PAGE ==============
const CharacterPage = () => {
  const { slug } = useParams();
  const [character, setCharacter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [heroRef, heroVisible] = useScrollReveal(0.1);
  const [letterRef, letterVisible] = useScrollReveal(0.1);
  const [galleryRef, galleryVisible] = useScrollReveal(0.1);
  
  const { content: siteSettings } = usePageContent('site_settings');

  useEffect(() => {
    const fetchCharacter = async () => {
      try {
        const response = await fetch(`${API}/characters/${slug}`);
        if (!response.ok) {
          throw new Error('Character not found');
        }
        const data = await response.json();
        setCharacter(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCharacter();
  }, [slug]);

  if (loading) {
    return (
      <div className="bg-charcoal-900 min-h-screen pt-24 flex items-center justify-center">
        <div className="text-parchment-300 font-serif">Loading...</div>
      </div>
    );
  }

  if (error || !character) {
    return (
      <div className="bg-charcoal-900 min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-3xl text-parchment-100 mb-4">Character Not Found</h1>
          <p className="font-serif text-parchment-400 mb-8">The character you seek has not yet revealed themselves.</p>
          <Link to="/characters" className="font-serif text-gold-400 hover:text-gold-300 transition-colors">
            ← Return to all characters
          </Link>
        </div>
      </div>
    );
  }

  // Hero is always visible (above fold), other sections use scroll reveal
  const heroAlwaysVisible = true;

  return (
    <div className="bg-charcoal-900 min-h-screen pt-24" data-testid="character-detail-page">
      {/* Identity Section */}
      <section ref={heroRef} className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div 
            className={`grid md:grid-cols-2 gap-12 lg:gap-16 items-start transition-all duration-1000 ${
              heroAlwaysVisible || heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            {/* Portrait */}
            <div className="aspect-[3/4] overflow-hidden rounded-sm">
              {character.portrait_url ? (
                <img
                  src={character.portrait_url}
                  alt={character.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-charcoal-800 flex items-center justify-center">
                  <span className="text-gold-400/30 text-8xl">?</span>
                </div>
              )}
            </div>
            
            {/* Identity Info */}
            <div className="py-4">
              {character.era && (
                <p className="font-serif text-gold-400 tracking-[0.3em] text-xs mb-4 uppercase">
                  {character.era}
                </p>
              )}
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-parchment-100 mb-6">
                {character.name}
              </h1>
              {character.short_bio && (
                <p className="font-serif text-parchment-300 text-lg leading-relaxed mb-8">
                  {character.short_bio}
                </p>
              )}
              {character.long_backstory && (
                <div 
                  className="font-serif text-parchment-400 leading-relaxed prose prose-invert prose-gold max-w-none"
                  dangerouslySetInnerHTML={{ __html: character.long_backstory }}
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* A Letter From Me Section */}
      {character.self_intro && (
        <section ref={letterRef} className="py-16 md:py-24 border-t border-gold-500/5">
          <div className="max-w-3xl mx-auto px-6">
            <div 
              className="transition-all duration-1000 opacity-100 translate-y-0"
            >
              <p className="font-serif text-gold-400 tracking-[0.3em] text-xs mb-6 text-center uppercase">
                A Letter From Me
              </p>
              <div className="bg-charcoal-800/30 border border-gold-500/10 rounded-sm p-8 md:p-12">
                <div className="font-serif text-parchment-200 text-lg leading-loose italic whitespace-pre-line">
                  {character.self_intro}
                </div>
                <p className="font-display text-gold-400 text-right mt-8 text-xl">
                  — {character.name}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Gallery Section */}
      {character.gallery_images && character.gallery_images.length > 0 && (
        <section ref={galleryRef} className="py-16 md:py-24 border-t border-gold-500/5">
          <div className="max-w-5xl mx-auto px-6">
            <div 
              className="transition-all duration-1000 opacity-100 translate-y-0"
            >
              <h2 className="font-display text-2xl md:text-3xl text-parchment-100 mb-12 text-center">
                Character Gallery
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {character.gallery_images.map((imageUrl, index) => (
                  <div 
                    key={index}
                    className="aspect-[4/5] overflow-hidden rounded-sm bg-charcoal-800"
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    <img
                      src={imageUrl}
                      alt={`${character.name} - Item ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 md:py-24 border-t border-gold-500/5">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="font-serif text-gold-400 tracking-[0.3em] text-xs mb-4 uppercase">
            Begin Your Correspondence
          </p>
          <h2 className="font-display text-3xl md:text-4xl text-parchment-100 mb-6">
            Receive Letters From {character.name}
          </h2>
          <p className="font-serif text-parchment-400 mb-10 max-w-xl mx-auto">
            Subscribe to begin receiving handwritten letters, artifacts, and secrets from beyond the veil of ordinary time.
          </p>
          <Link
            to="/membership"
            data-testid="character-subscribe-cta"
            className="inline-flex items-center justify-center gap-3 bg-gold-500/10 hover:bg-gold-500/20 border border-gold-500/30 hover:border-gold-400/60 text-gold-400 font-serif text-base px-10 py-4 rounded-sm transition-all duration-500"
          >
            Subscribe Now
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </section>

      <SiteFooter siteSettings={siteSettings} />
    </div>
  );
};

// ============== SHOP PAGE ==============
const Shop = () => {
  const [heroRef, heroVisible] = useScrollReveal(0.1);

  const { content: pageContent } = usePageContent('shop');
  const { content: siteSettings } = usePageContent('site_settings');
  const { products } = useProducts();

  const handleCheckout = async (productId) => {
    try {
      const response = await fetch(`${API}/checkout/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          product_ids: [productId],
          origin_url: window.location.origin
        })
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.detail || 'Checkout failed');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Unable to start checkout. Please try again.');
    }
  };

  return (
    <div className="bg-charcoal-900 min-h-screen pt-24" data-testid="shop-page">
      <section ref={heroRef} className="py-16 md:py-24">
        <div 
          className={`max-w-4xl mx-auto px-6 text-center transition-all duration-1000 ${
            heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="font-serif text-gold-400 tracking-[0.3em] text-xs mb-4">
            {pageContent.page_label || 'TREASURES FROM THE STORY'}
          </p>
          <h1 className="font-display text-4xl md:text-6xl text-parchment-100 mb-6">
            {pageContent.title || 'The Shop'}
          </h1>
          <p className="font-serif text-parchment-300 text-lg leading-relaxed max-w-2xl mx-auto mb-12">
            {pageContent.description || 'Artifacts, collections, and special editions from the world of The Timeless Prophecy.'}
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6">
          {products.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-8">
              {products.map((product) => (
                <div 
                  key={product.id}
                  className="bg-charcoal-800/50 border border-gold-500/20 rounded-sm overflow-hidden group"
                >
                  <div className="aspect-[3/4] overflow-hidden">
                    {product.image_url ? (
                      <img
                        src={product.image_url}
                        alt={product.title}
                        className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                        style={{backgroundColor: '#F5EFE6'}}
                      />
                    ) : (
                      <div className="w-full h-full bg-charcoal-700/50 flex items-center justify-center">
                        <svg className="w-16 h-16 text-gold-500/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-xl text-parchment-100 mb-2">{product.title}</h3>
                    <p className="font-serif text-parchment-300 text-sm mb-4 line-clamp-2">{product.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="font-display text-2xl text-gold-400">${product.price.toFixed(2)}</span>
                      <button
                        onClick={() => handleCheckout(product.id)}
                        className="font-serif text-sm text-gold-400 hover:text-gold-300 transition-colors"
                      >
                        Purchase →
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((item) => (
                <div 
                  key={item}
                  className="aspect-[3/4] rounded-sm border border-gold-500/20 bg-charcoal-800/30 flex items-center justify-center"
                >
                  <div className="text-center p-6">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-charcoal-700/50 flex items-center justify-center">
                      <svg className="w-8 h-8 text-gold-500/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </div>
                    <p className="font-serif text-parchment-400 text-sm">Coming Soon</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {products.length === 0 && (
            <div className="text-center mt-16">
              <p className="font-serif text-parchment-300 italic">
                {pageContent.coming_soon_message || 'The shop is being prepared. Sign up for membership to be notified when treasures become available.'}
              </p>
            </div>
          )}
        </div>
      </section>

      <SiteFooter siteSettings={siteSettings} />
    </div>
  );
};

// ============== ABOUT PAGE ==============
const About = () => {
  const [heroRef, heroVisible] = useScrollReveal(0.1);

  const { content: pageContent } = usePageContent('about');
  const { content: siteSettings } = usePageContent('site_settings');

  return (
    <div className="bg-charcoal-900 min-h-screen pt-24" data-testid="about-page">
      <section ref={heroRef} className="py-16 md:py-24">
        <div 
          className={`max-w-3xl mx-auto px-6 transition-all duration-1000 ${
            heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="font-serif text-gold-400 tracking-[0.3em] text-xs mb-4 text-center">
            {pageContent.page_label || 'THE STORY BEHIND THE STORY'}
          </p>
          <h1 className="font-display text-4xl md:text-6xl text-parchment-100 mb-12 text-center">
            {pageContent.title || 'About'}
          </h1>
          
          <div className="prose prose-invert max-w-none">
            <p className="font-serif text-parchment-300 text-lg leading-relaxed mb-8">
              {pageContent.intro || 'The Timeless Post was born from a simple belief: that stories deserve to be experienced, not just read.'}
            </p>
            
            <p className="font-serif text-parchment-300 text-lg leading-relaxed mb-8">
              {pageContent.philosophy || 'We create immersive storytelling experiences delivered through the timeless medium of handwritten correspondence.'}
            </p>
            
            <p className="font-serif text-parchment-300 text-lg leading-relaxed mb-8">
              {pageContent.mission || 'The Timeless Prophecy is our flagship experience—a world of seers and scholars, of secrets written in starlight.'}
            </p>
            
            {pageContent.quote && (
              <div className="border-l-2 border-gold-500/40 pl-6 my-12">
                <p className="font-display text-2xl text-parchment-100 italic mb-4">
                  "{pageContent.quote}"
                </p>
              </div>
            )}
            
            <p className="font-serif text-parchment-300 text-lg leading-relaxed">
              {pageContent.closing || 'Thank you for believing in the magic of stories told slowly, beautifully, and with intention.'}
            </p>
          </div>
        </div>
      </section>

      <SiteFooter siteSettings={siteSettings} />
    </div>
  );
};

// ============== ABOUT THE AUTHOR PAGE ==============
const AboutAuthor = () => {
  const [heroRef, heroVisible] = useScrollReveal(0.1);
  const { content: pageContent } = usePageContent('about_author');
  const { content: siteSettings } = usePageContent('site_settings');

  return (
    <div className="bg-charcoal-900 min-h-screen pt-24" data-testid="author-page">
      <section ref={heroRef} className="py-16 md:py-24">
        <div 
          className={`max-w-3xl mx-auto px-6 transition-all duration-1000 ${
            heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="font-serif text-gold-400 tracking-[0.3em] text-xs mb-4 text-center">
            {pageContent.page_label || 'THE STORYTELLER'}
          </p>
          <h1 className="font-display text-4xl md:text-6xl text-parchment-100 mb-12 text-center">
            {pageContent.title || 'About the Author'}
          </h1>
          
          {/* Author Photo */}
          {pageContent.author_photo && (
            <div className="flex justify-center mb-12">
              <img 
                src={pageContent.author_photo} 
                alt="Author" 
                className="w-48 h-48 md:w-64 md:h-64 rounded-full object-cover border-2 border-gold-500/30 shadow-lg"
              />
            </div>
          )}
          
          <div className="prose prose-invert max-w-none">
            {pageContent.body_html ? (
              <div 
                className="font-serif text-parchment-300 text-lg leading-relaxed [&>p]:mb-6 [&>h2]:font-display [&>h2]:text-2xl [&>h2]:text-parchment-100 [&>h2]:mt-8 [&>h2]:mb-4 [&>h3]:font-display [&>h3]:text-xl [&>h3]:text-parchment-100 [&>h3]:mt-6 [&>h3]:mb-3 [&>ul]:list-disc [&>ul]:ml-6 [&>ul]:mb-6 [&>li]:mb-2 [&>img]:rounded-lg [&>img]:my-6 [&>img]:mx-auto [&>img]:max-w-full [&>img]:border [&>img]:border-gold-500/20"
                dangerouslySetInnerHTML={{ __html: pageContent.body_html }}
              />
            ) : (
              <>
                <p className="font-serif text-parchment-300 text-lg leading-relaxed mb-8">
                  {pageContent.intro || 'Every story begins with a storyteller.'}
                </p>
                <p className="font-serif text-parchment-300 text-lg leading-relaxed">
                  {pageContent.bio || 'Content coming soon. Check back for the story behind the stories.'}
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      <SiteFooter siteSettings={siteSettings} />
    </div>
  );
};

// ============== FAQ PAGE ==============
const FAQ = () => {
  const [heroRef, heroVisible] = useScrollReveal(0.1);
  const { content: pageContent } = usePageContent('faq');
  const { content: siteSettings } = usePageContent('site_settings');

  return (
    <div className="bg-charcoal-900 min-h-screen pt-24" data-testid="faq-page">
      <section ref={heroRef} className="py-16 md:py-24">
        <div 
          className={`max-w-3xl mx-auto px-6 transition-all duration-1000 ${
            heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="font-serif text-gold-400 tracking-[0.3em] text-xs mb-4 text-center">
            {pageContent.page_label || 'QUESTIONS & ANSWERS'}
          </p>
          <h1 className="font-display text-4xl md:text-6xl text-parchment-100 mb-12 text-center">
            {pageContent.title || 'Frequently Asked Questions'}
          </h1>
          
          <div className="prose prose-invert max-w-none">
            {pageContent.intro && (
              <p className="font-serif text-parchment-300 text-lg leading-relaxed mb-8 text-center">
                {pageContent.intro}
              </p>
            )}
            {pageContent.body_html ? (
              <div 
                className="font-serif text-parchment-300 text-lg leading-relaxed [&>p]:mb-6 [&>h2]:font-display [&>h2]:text-2xl [&>h2]:text-parchment-100 [&>h2]:mt-8 [&>h2]:mb-4 [&>h3]:font-display [&>h3]:text-xl [&>h3]:text-gold-400 [&>h3]:mt-6 [&>h3]:mb-3 [&>ul]:list-disc [&>ul]:ml-6 [&>ul]:mb-6 [&>li]:mb-2 [&>strong]:text-parchment-100"
                dangerouslySetInnerHTML={{ __html: pageContent.body_html }}
              />
            ) : (
              <p className="font-serif text-parchment-300 text-lg leading-relaxed text-center">
                Questions? We have answers. Check back soon for our FAQ.
              </p>
            )}
          </div>
        </div>
      </section>

      <SiteFooter siteSettings={siteSettings} />
    </div>
  );
};

// ============== READER REFLECTIONS (REVIEWS) PAGE ==============
const ReaderReflections = () => {
  const [heroRef, heroVisible] = useScrollReveal(0.1);
  const { content: pageContent } = usePageContent('reviews');
  const { content: siteSettings } = usePageContent('site_settings');

  return (
    <div className="bg-charcoal-900 min-h-screen pt-24" data-testid="reviews-page">
      <section ref={heroRef} className="py-16 md:py-24">
        <div 
          className={`max-w-3xl mx-auto px-6 transition-all duration-1000 ${
            heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <p className="font-serif text-gold-400 tracking-[0.3em] text-xs mb-4 text-center">
            {pageContent.page_label || 'VOICES FROM OUR READERS'}
          </p>
          <h1 className="font-display text-4xl md:text-6xl text-parchment-100 mb-12 text-center">
            {pageContent.title || 'Reader Reflections'}
          </h1>

          {/* Hero Image */}
          {pageContent.hero_image && (
            <div className="flex justify-center mb-12">
              <img
                src={pageContent.hero_image}
                alt="Reader Reflections"
                className="w-full max-w-2xl rounded-lg object-cover border border-gold-500/20 shadow-lg"
                loading="lazy"
              />
            </div>
          )}
          
          <div className="prose prose-invert max-w-none">
            {pageContent.intro && (
              <p className="font-serif text-parchment-300 text-lg leading-relaxed mb-8 text-center">
                {pageContent.intro}
              </p>
            )}
            {pageContent.body_html ? (
              <div 
                className="font-serif text-parchment-300 text-lg leading-relaxed [&>p]:mb-6 [&>h2]:font-display [&>h2]:text-2xl [&>h2]:text-parchment-100 [&>h2]:mt-8 [&>h2]:mb-4 [&>h3]:font-display [&>h3]:text-xl [&>h3]:text-parchment-100 [&>h3]:mt-6 [&>h3]:mb-3 [&>blockquote]:border-l-2 [&>blockquote]:border-gold-500/40 [&>blockquote]:pl-6 [&>blockquote]:italic [&>blockquote]:my-6 [&>ul]:list-disc [&>ul]:ml-6 [&>ul]:mb-6 [&>li]:mb-2 [&>img]:rounded-lg [&>img]:my-6 [&>img]:mx-auto [&>img]:max-w-full [&>img]:border [&>img]:border-gold-500/20"
                dangerouslySetInnerHTML={{ __html: pageContent.body_html }}
              />
            ) : (
              <p className="font-serif text-parchment-300 text-lg leading-relaxed text-center">
                Stories from those who have received their letters. Coming soon.
              </p>
            )}
          </div>
        </div>
      </section>

      <SiteFooter siteSettings={siteSettings} />
    </div>
  );
};

// ============== POLICY PAGE WRAPPER ==============
const PolicyPageCMS = ({ pageKey, defaultTitle, defaultLabel, defaultContent }) => {
  const { content: pageContent } = usePageContent(pageKey);
  const { content: siteSettings } = usePageContent('site_settings');
  
  const title = pageContent.title || defaultTitle;
  const bodyHtml = pageContent.body_html || defaultContent;
  const lastUpdated = pageContent.last_updated || 'February 2025';
  
  return (
    <div className="bg-charcoal-900 min-h-screen pt-24">
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6">
          <p className="font-serif text-gold-400 tracking-[0.3em] text-xs mb-4 text-center">
            {defaultLabel}
          </p>
          <h1 className="font-display text-4xl md:text-5xl text-parchment-100 mb-12 text-center">
            {title}
          </h1>
          
          <div 
            className="font-serif text-parchment-300 leading-relaxed policy-content"
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />
          
          <p className="font-serif text-parchment-400 text-sm mt-12">Last updated: {lastUpdated}</p>
        </div>
      </section>

      <SiteFooter siteSettings={siteSettings} />
    </div>
  );
};

// ============== CUSTOM PAGE (DYNAMIC) ==============
const CustomPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { content: siteSettings } = usePageContent('site_settings');
  
  const API = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const response = await fetch(`${API}/api/custom-pages/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setPage(data);
          // Update document title
          document.title = data.seo_title || data.title || 'Page';
        } else if (response.status === 404) {
          setError('Page not found');
        } else {
          setError('Failed to load page');
        }
      } catch (err) {
        setError('Failed to load page');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPage();
  }, [slug, API]);

  if (loading) {
    return (
      <div className="bg-charcoal-900 min-h-screen pt-24 flex items-center justify-center">
        <div className="text-parchment-400">Loading...</div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="bg-charcoal-900 min-h-screen pt-24">
        <section className="py-16 md:py-24">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h1 className="font-display text-4xl text-parchment-100 mb-6">Page Not Found</h1>
            <p className="font-serif text-parchment-400 mb-8">
              The page you're looking for doesn't exist or has been moved.
            </p>
            <button
              onClick={() => navigate('/')}
              className="font-serif text-gold-400 hover:text-gold-300 transition-colors"
            >
              ← Return to Home
            </button>
          </div>
        </section>
        <SiteFooter siteSettings={siteSettings} />
      </div>
    );
  }

  return (
    <div className="bg-charcoal-900 min-h-screen pt-24">
      {/* Meta description for SEO */}
      {page.meta_description && (
        <meta name="description" content={page.meta_description} />
      )}
      
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6">
          {/* Decorative divider */}
          <div className="text-center mb-8">
            <span className="text-gold-500/60 tracking-[0.5em] text-sm font-light">✧ ✧ ✧</span>
          </div>
          
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl text-parchment-100 mb-12 text-center">
            {page.title}
          </h1>
          
          <div 
            className="font-serif text-parchment-300 leading-relaxed policy-content prose prose-invert max-w-none
              prose-headings:font-display prose-headings:text-parchment-100
              prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
              prose-p:mb-4 prose-p:text-parchment-300/90
              prose-ul:my-4 prose-li:text-parchment-300/90
              prose-strong:text-parchment-100
              prose-a:text-gold-400 prose-a:no-underline hover:prose-a:text-gold-300"
            dangerouslySetInnerHTML={{ __html: page.content_html }}
          />
          
          {/* Bottom decorative divider */}
          <div className="text-center mt-16">
            <span className="text-gold-500/60 tracking-[0.5em] text-sm font-light">✧ ✧ ✧</span>
          </div>
        </div>
      </section>

      <SiteFooter siteSettings={siteSettings} />
    </div>
  );
};

// ============== TERMS AND CONDITIONS PAGE ==============
const TermsAndConditions = () => (
  <PolicyPageCMS 
    pageKey="policy_terms"
    defaultTitle="Terms & Conditions"
    defaultLabel="LEGAL"
    defaultContent={`
      <p>Welcome to The Timeless Post. By accessing or using our website and services, you agree to be bound by these Terms and Conditions.</p>
      
      <h2>1. Services</h2>
      <p>The Timeless Post provides immersive storytelling experiences through digital and physical letter subscriptions. Our services include character-driven correspondence delivered via email or postal mail.</p>
      
      <h2>2. Subscriptions</h2>
      <p>Subscription fees are billed on a recurring basis according to the plan selected. You may cancel your subscription at any time through your account or by contacting us. Cancellation will take effect at the end of your current billing period.</p>
      
      <h2>3. User Accounts</h2>
      <p>You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.</p>
      
      <h2>4. Intellectual Property</h2>
      <p>All content, characters, stories, artwork, and materials on this website are the intellectual property of The Timeless Post and are protected by copyright law. You may not reproduce, distribute, or create derivative works without our express written permission.</p>
      
      <h2>5. Limitation of Liability</h2>
      <p>The Timeless Post provides its services "as is" and makes no warranties regarding the availability or performance of our services. We shall not be liable for any indirect, incidental, or consequential damages.</p>
      
      <h2>6. Changes to Terms</h2>
      <p>We reserve the right to modify these terms at any time. Continued use of our services after changes constitutes acceptance of the modified terms.</p>
      
      <h2>7. Governing Law</h2>
      <p>These terms are governed by the laws of Australia. Any disputes shall be resolved in Australian courts.</p>
    `}
  />
);

// ============== PRIVACY POLICY PAGE ==============
const PrivacyPolicy = () => (
  <PolicyPageCMS 
    pageKey="policy_privacy"
    defaultTitle="Privacy Policy"
    defaultLabel="YOUR DATA"
    defaultContent={`
      <p>The Timeless Post respects your privacy and is committed to protecting your personal information. This policy explains how we collect, use, and safeguard your data.</p>
      
      <h2>Information We Collect</h2>
      <ul>
        <li>Name and email address when you create an account</li>
        <li>Shipping address for physical letter subscriptions</li>
        <li>Payment information (processed securely through Stripe)</li>
        <li>Character preferences and subscription choices</li>
      </ul>
      
      <h2>How We Use Your Information</h2>
      <ul>
        <li>To deliver your subscription content and letters</li>
        <li>To process payments and manage your account</li>
        <li>To send service-related communications</li>
        <li>To improve our services and user experience</li>
      </ul>
      
      <h2>Data Protection</h2>
      <p>We implement appropriate security measures to protect your personal information. Payment processing is handled by Stripe, a PCI-compliant payment processor. We do not store your full credit card details.</p>
      
      <h2>Third-Party Services</h2>
      <p>We use trusted third-party services including Stripe for payments and SendGrid for email delivery. These services have their own privacy policies governing their handling of your data.</p>
      
      <h2>Your Rights</h2>
      <p>You have the right to access, correct, or delete your personal information. You may also opt out of marketing communications at any time. Contact us at letters@the-timeless-post.com to exercise these rights.</p>
      
      <h2>Cookies</h2>
      <p>We use essential cookies to maintain your session and preferences. We do not use advertising or tracking cookies.</p>
    `}
  />
);

// ============== REFUND POLICY PAGE ==============
const RefundPolicy = () => (
  <PolicyPageCMS 
    pageKey="policy_refund"
    defaultTitle="Refund Policy"
    defaultLabel="PURCHASES"
    defaultContent={`
      <p>We want you to be delighted with your experience at The Timeless Post. If you're not satisfied, we're here to help.</p>
      
      <h2>Digital Subscriptions</h2>
      <p>You may cancel your digital subscription at any time. Cancellation will take effect at the end of your current billing period. As digital content is delivered immediately, we generally do not offer refunds for the current billing period.</p>
      <p>If you experience technical issues preventing access to your content, please contact us and we will resolve the issue or provide a refund.</p>
      
      <h2>Physical Letter Subscriptions</h2>
      <p>You may cancel your physical letter subscription at any time. Cancellation will take effect at the end of your current billing period.</p>
      <p>If your letters arrive damaged or do not arrive at all, please contact us within 14 days and we will send a replacement or provide a refund.</p>
      
      <h2>Gift Subscriptions</h2>
      <p>Gift subscriptions may be refunded in full if the recipient has not yet redeemed or received any content. Once content has been delivered, our standard refund policy applies.</p>
      
      <h2>How to Request a Refund</h2>
      <p>To request a refund, please contact us at letters@the-timeless-post.com with your order details. We aim to process all refund requests within 5-7 business days.</p>
      
      <h2>Exceptions</h2>
      <p>We reserve the right to refuse refunds in cases of abuse or fraud.</p>
    `}
  />
);

// ============== SHIPPING POLICY PAGE ==============
const ShippingPolicy = () => (
  <PolicyPageCMS 
    pageKey="policy_shipping"
    defaultTitle="Shipping Policy"
    defaultLabel="DELIVERY"
    defaultContent={`
      <p>This policy applies to physical letter subscriptions only. Digital subscriptions are delivered via email.</p>
      
      <h2>Shipping Schedule</h2>
      <p>Physical letters are shipped once per month. All characters you've selected under one subscription are shipped together in a single envelope.</p>
      
      <h2>Shipping Locations</h2>
      <p>We currently ship to Australia, United States, United Kingdom, Canada, and select European countries. Shipping costs and delivery times vary by location.</p>
      
      <h2>Delivery Times</h2>
      <ul>
        <li><strong>Australia:</strong> 3-7 business days</li>
        <li><strong>United States:</strong> 10-21 business days</li>
        <li><strong>United Kingdom:</strong> 10-21 business days</li>
        <li><strong>Canada:</strong> 10-21 business days</li>
        <li><strong>Europe:</strong> 14-28 business days</li>
      </ul>
      
      <h2>Tracking</h2>
      <p>Standard shipping does not include tracking. For tracked shipping options, please contact us before your order ships.</p>
      
      <h2>Lost or Damaged Items</h2>
      <p>If your letters do not arrive within the expected timeframe or arrive damaged, please contact us at letters@the-timeless-post.com. We will investigate and send a replacement if necessary.</p>
      
      <h2>Address Changes</h2>
      <p>Please ensure your shipping address is correct before each billing cycle. Address changes must be made at least 7 days before your next shipping date.</p>
    `}
  />
);

// ============== CONTACT PAGE ==============
const Contact = () => {
  const { content: pageContent } = usePageContent('contact');
  const { content: siteSettings } = usePageContent('site_settings');
  
  const contactEmail = siteSettings.contact_email || 'letters@the-timeless-post.com';
  const location = siteSettings.location || 'Based in Australia';
  
  return (
    <div className="bg-charcoal-900 min-h-screen pt-24" data-testid="contact-page">
      <section className="py-16 md:py-24">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <p className="font-serif text-gold-400 tracking-[0.3em] text-xs mb-4">
            GET IN TOUCH
          </p>
          <h1 className="font-display text-4xl md:text-5xl text-parchment-100 mb-8">
            {pageContent.title || 'Contact Us'}
          </h1>
          
          <p className="font-serif text-parchment-300 text-lg leading-relaxed mb-12">
            {pageContent.intro || 'Have a question about your subscription, need help with your account, or simply want to share your thoughts? We\'d love to hear from you.'}
          </p>
          
          <div className="bg-charcoal-800/50 border border-gold-500/20 rounded-sm p-8 md:p-12">
            <p className="font-serif text-parchment-400 text-sm mb-4">Reach us at</p>
            <a 
              href={`mailto:${contactEmail}`}
              className="font-display text-2xl md:text-3xl text-gold-400 hover:text-gold-300 transition-colors"
            >
              {contactEmail}
            </a>
            <p className="font-serif text-parchment-400 text-sm mt-6">
              {pageContent.response_time || 'We typically respond within 24–48 hours.'}
            </p>
          </div>
          
          <p className="font-serif text-parchment-400 text-sm mt-12">
            {location}
          </p>
        </div>
      </section>

      <SiteFooter siteSettings={siteSettings} />
    </div>
  );
};

// ============== SIGN IN PAGE ==============
const SignIn = () => {
  const { content: siteSettings } = usePageContent('site_settings');

  return (
    <div className="bg-charcoal-900 min-h-screen pt-24 flex items-center justify-center" data-testid="signin-page">
      <div className="max-w-md w-full mx-auto px-6">
        <div className="text-center mb-12">
          <p className="font-serif text-gold-400 tracking-[0.3em] text-xs mb-4">
            {siteSettings.signin_welcome || 'WELCOME BACK'}
          </p>
          <h1 className="font-display text-4xl text-parchment-100 mb-4">
            {siteSettings.signin_title || 'Sign In'}
          </h1>
          <p className="font-serif text-parchment-300">
            {siteSettings.signin_description || 'Return to your correspondence.'}
          </p>
        </div>
        
        <div className="space-y-6">
          <div>
            <label className="block font-serif text-parchment-300 text-sm mb-2">Email</label>
            <input 
              type="email" 
              className="w-full bg-charcoal-800 border border-gold-500/20 rounded px-4 py-3 font-serif text-parchment-100 focus:outline-none focus:border-gold-400 transition-colors"
              placeholder="your@email.com"
            />
          </div>
          <div>
            <label className="block font-serif text-parchment-300 text-sm mb-2">Password</label>
            <input 
              type="password" 
              className="w-full bg-charcoal-800 border border-gold-500/20 rounded px-4 py-3 font-serif text-parchment-100 focus:outline-none focus:border-gold-400 transition-colors"
              placeholder="••••••••"
            />
          </div>
          <button className="w-full bg-gold-500 hover:bg-gold-400 text-parchment-100 font-serif py-3 rounded transition-all duration-300">
            Enter
          </button>
          
          <p className="text-center font-serif text-parchment-400 text-sm">
            {siteSettings.signin_new_user_text || 'New to The Timeless Post?'}{' '}
            <Link to="/membership" className="text-gold-400 hover:text-gold-300 transition-colors">
              {siteSettings.signin_new_user_link || 'Begin your journey'}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

// ============== CHECKOUT SUCCESS ==============
const CheckoutSuccess = () => {
  const [status, setStatus] = useState('checking');
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (!sessionId) {
      setStatus('success'); // No session ID, assume success
      return;
    }
    
    const pollStatus = async (attempts = 0) => {
      const maxAttempts = 5;
      
      if (attempts >= maxAttempts) {
        setStatus('success'); // Assume success after max attempts
        return;
      }
      
      try {
        const response = await fetch(`${API}/checkout/status/${sessionId}`);
        const data = await response.json();
        
        if (data.payment_status === 'paid') {
          setStatus('success');
        } else if (data.status === 'expired') {
          setError('Payment session expired');
          setStatus('error');
        } else {
          // Continue polling
          setTimeout(() => pollStatus(attempts + 1), 2000);
        }
      } catch (err) {
        console.error('Status check error:', err);
        setStatus('success'); // Assume success on error
      }
    };
    
    pollStatus();
  }, []);

  if (status === 'checking') {
    return (
      <div className="bg-charcoal-900 min-h-screen pt-24 flex items-center justify-center">
        <div className="max-w-md w-full mx-auto px-6 text-center">
          <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-gold-500/20 flex items-center justify-center animate-pulse">
            <svg className="w-10 h-10 text-gold-400 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <h1 className="font-display text-3xl text-parchment-100 mb-4">Processing Payment...</h1>
          <p className="font-serif text-parchment-300">Please wait while we confirm your order.</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="bg-charcoal-900 min-h-screen pt-24 flex items-center justify-center">
        <div className="max-w-md w-full mx-auto px-6 text-center">
          <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-red-900/30 flex items-center justify-center">
            <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="font-display text-3xl text-parchment-100 mb-4">Payment Issue</h1>
          <p className="font-serif text-parchment-300 mb-8">{error || 'Something went wrong with your payment.'}</p>
          <Link
            to="/membership"
            className="inline-block bg-gold-500 hover:bg-gold-400 text-parchment-100 font-serif px-8 py-3 rounded transition-colors"
          >
            Try Again
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-charcoal-900 min-h-screen pt-24 flex items-center justify-center">
      <div className="max-w-md w-full mx-auto px-6 text-center">
        <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-green-900/30 flex items-center justify-center">
          <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="font-display text-3xl text-parchment-100 mb-4">Thank You!</h1>
        <p className="font-serif text-parchment-300 mb-8">
          Your order has been confirmed. The characters are preparing to write to you.
        </p>
        <Link
          to="/"
          className="inline-block bg-gold-500 hover:bg-gold-400 text-parchment-100 font-serif px-8 py-3 rounded transition-colors"
        >
          Return Home
        </Link>
      </div>
    </div>
  );
};

// ============== FREE LETTER KIT PAGE ==============
const FreeLetterKitPage = () => {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({ name: '', email: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [heroRef, heroVisible] = useScrollReveal(0.1);
  const [lightboxImg, setLightboxImg] = useState(null);

  useEffect(() => {
    fetch(`${API}/freebie/letter-kit`).then(r => r.json()).then(setConfig).catch(() => {});
  }, []);

  const [submitError, setSubmitError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const response = await fetch(`${API}/freebie/letter-kit/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `Submission failed (${response.status})`);
      }
      const result = await response.json();
      if (result.saved) {
        setSubmitted(true);
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('Freebie submit error:', err);
      setSubmitError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const headline = config?.headline || 'Your Free Vintage Letter Writing Kit';
  const description = config?.description || 'A beautifully designed collection of vintage letter templates, wax seal guides, and penmanship tips — yours free. Enter your details below and we\'ll send it straight to your inbox.';
  const formEnabled = config?.form_enabled !== false;
  const imgBase = BACKEND_URL || '';

  // Image data from config
  const heroImage = config?.hero_image?.visible !== false ? config?.hero_image : null;
  const galleryImages = (config?.gallery_images || []).filter(img => img.visible !== false);
  const featuredImg = galleryImages.find(img => img.featured);
  const secondaryImage = config?.secondary_image?.visible !== false ? config?.secondary_image : null;

  return (
    <div className="bg-charcoal-900 min-h-screen pt-24" data-testid="free-letter-kit-page">
      {/* Hero Image */}
      {heroImage?.url && (
        <div className="max-w-4xl mx-auto px-6 pt-8" data-testid="freebie-hero-image-section">
          <div className="relative overflow-hidden rounded-xl shadow-2xl shadow-black/40">
            <img
              src={`${imgBase}${heroImage.url}`}
              alt={heroImage.caption || headline}
              loading="lazy"
              className="w-full h-auto max-h-[500px] object-cover"
              data-testid="freebie-hero-image"
            />
            {heroImage.caption && (
              <p className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-parchment-100/60 to-transparent px-4 py-3 font-serif text-parchment-200 text-sm text-center">
                {heroImage.caption}
              </p>
            )}
          </div>
        </div>
      )}

      <section ref={heroRef} className="py-20 md:py-28">
        <div className={`max-w-2xl mx-auto px-6 text-center transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <p className="font-serif text-gold-400 tracking-[0.3em] text-xs mb-5">COMPLIMENTARY DOWNLOAD</p>
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl text-parchment-100 mb-6" data-testid="freebie-headline">
            {headline}
          </h1>
          <p className="font-serif text-parchment-300 text-base md:text-lg leading-relaxed max-w-xl mx-auto mb-4">
            {description}
          </p>

          {/* What's included */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10 mb-12 text-left max-w-lg mx-auto">
            {[
              { title: 'Letter Templates', desc: 'Elegant vintage-style templates ready to print and use' },
              { title: 'Wax Seal Guide', desc: 'Step-by-step instructions for beautiful wax seals' },
              { title: 'Penmanship Tips', desc: 'Techniques to elevate your handwriting' },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-3">
                <svg className="w-5 h-5 text-gold-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <p className="font-display text-sm text-parchment-100">{item.title}</p>
                  <p className="font-serif text-parchment-500 text-xs">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Gallery Section — below What's Included */}
          {galleryImages.length > 0 && (
            <div className="mb-14" data-testid="freebie-gallery-section">
              <p className="font-serif text-gold-400 tracking-[0.3em] text-xs mb-6">A GLIMPSE INSIDE</p>
              <div className={`grid gap-3 ${galleryImages.length === 1 ? 'grid-cols-1' : galleryImages.length === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3'}`}>
                {galleryImages.map((img, idx) => {
                  const isFeatured = img.featured || (idx === 0 && !featuredImg);
                  return (
                    <div
                      key={img.id || idx}
                      className={`relative group cursor-pointer overflow-hidden rounded-lg border border-gold-500/10 hover:border-gold-500/30 transition-all duration-300 ${isFeatured && galleryImages.length > 2 ? 'sm:col-span-2 sm:row-span-2' : ''}`}
                      onClick={() => setLightboxImg(img)}
                      data-testid={`freebie-gallery-item-${idx}`}
                    >
                      <img
                        src={`${imgBase}${img.url}`}
                        alt={img.caption || `Gallery image ${idx + 1}`}
                        loading="lazy"
                        className={`w-full object-cover transition-transform duration-500 group-hover:scale-105 ${isFeatured && galleryImages.length > 2 ? 'h-full min-h-[200px] sm:min-h-[300px]' : 'h-48 sm:h-56'}`}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-parchment-100/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                        <div className="p-3 w-full">
                          {img.caption && <p className="font-serif text-parchment-100 text-sm">{img.caption}</p>}
                          <p className="font-serif text-parchment-400 text-xs mt-1">Click to enlarge</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Secondary Image — between gallery and form */}
          {secondaryImage?.url && (
            <div className="mb-12" data-testid="freebie-secondary-image-section">
              <div className="relative overflow-hidden rounded-xl border border-gold-500/10">
                <img
                  src={`${imgBase}${secondaryImage.url}`}
                  alt={secondaryImage.caption || 'Letter writing detail'}
                  loading="lazy"
                  className="w-full h-auto max-h-[350px] object-cover"
                  data-testid="freebie-secondary-image"
                />
                {secondaryImage.caption && (
                  <p className="bg-charcoal-800/80 px-4 py-2 font-serif text-parchment-300 text-sm text-center">
                    {secondaryImage.caption}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Form or success */}
          {submitted ? (
            <div className="bg-charcoal-800/60 border border-gold-500/20 rounded-lg p-8 max-w-md mx-auto" data-testid="freebie-success">
              <svg className="w-12 h-12 text-gold-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <h3 className="font-display text-xl text-parchment-100 mb-2">Your letter kit is on its way to your inbox.</h3>
              <p className="font-serif text-parchment-400 text-sm">If you don't see it within a few minutes, check your spam or promotions folder.</p>
            </div>
          ) : formEnabled ? (
            <form onSubmit={handleSubmit} className="max-w-md mx-auto" data-testid="freebie-form">
              {submitError && (
                <div className="mb-4 p-3 bg-red-900/30 border border-red-500/30 rounded-lg text-red-300 font-serif text-sm" data-testid="freebie-submit-error">
                  {submitError}
                </div>
              )}
              <div className="space-y-3 mb-4">
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your first name"
                  data-testid="freebie-name-input"
                  className="w-full bg-charcoal-800 border border-gold-500/20 rounded-lg px-4 py-3 text-parchment-100 font-serif focus:outline-none focus:border-gold-500/50 placeholder:text-parchment-600"
                />
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Your email address"
                  required
                  data-testid="freebie-email-input"
                  className="w-full bg-charcoal-800 border border-gold-500/20 rounded-lg px-4 py-3 text-parchment-100 font-serif focus:outline-none focus:border-gold-500/50 placeholder:text-parchment-600"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="freebie-submit-btn"
                className="w-full py-3.5 bg-gradient-to-r from-gold-600 to-gold-500 hover:from-gold-500 hover:to-gold-400 text-parchment-100 font-display text-sm tracking-widest uppercase rounded-lg transition-all duration-300 disabled:opacity-50"
              >
                {submitting ? 'Sending...' : 'Receive Your Letter Kit'}
              </button>
              <p className="font-serif text-parchment-600 text-xs mt-3">No spam, ever. Unsubscribe anytime.</p>
            </form>
          ) : (
            <p className="font-serif text-parchment-400">This offer is currently unavailable. Check back soon.</p>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImg && (
        <div
          className="fixed inset-0 z-50 bg-parchment-100/80 backdrop-blur-sm flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setLightboxImg(null)}
          data-testid="freebie-lightbox"
        >
          <button
            className="absolute top-6 right-6 text-parchment-400 hover:text-parchment-100 text-3xl z-10"
            onClick={() => setLightboxImg(null)}
            data-testid="freebie-lightbox-close"
          >
            &times;
          </button>
          <div className="relative max-w-5xl max-h-[85vh] w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={`${imgBase}${lightboxImg.url}`}
              alt={lightboxImg.caption || 'Gallery image'}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
              data-testid="freebie-lightbox-image"
            />
            {lightboxImg.caption && (
              <p className="mt-3 font-serif text-parchment-200 text-sm text-center">{lightboxImg.caption}</p>
            )}
          </div>
        </div>
      )}

      <SiteFooter />
    </div>
  );
};

// ============== GIFT PAGE ==============
const GiftPage = () => {
  const [heroRef, heroVisible] = useScrollReveal(0.1);
  const { memberships } = useMemberships();
  const navigate = useNavigate();
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [selectedGiftTier, setSelectedGiftTier] = useState(null);
  const [giftForm, setGiftForm] = useState({ email: '', message: '' });

  const handleSelectTier = (membership, options = {}) => {
    const isPrepaid = membership.is_prepaid || membership.category === 'prepaid';
    const needsCharacterSelection = membership.character_count > 0 && membership.category !== 'mystery';

    const baseParams = {
      tier_id: membership.id,
      tier_name: membership.name,
      character_count: String(needsCharacterSelection ? (membership.character_count || 1) : 0),
      subscription_type: membership.subscription_type || 'digital',
      price: membership.price,
      billing_cycle: membership.billing_cycle,
      ...(options.is_gift && { is_gift: 'true' }),
      ...(isPrepaid && { is_prepaid: 'true' }),
      ...(options.gift_recipient_email && { gift_recipient_email: options.gift_recipient_email }),
      ...(options.gift_message && { gift_message: options.gift_message }),
    };

    if (!needsCharacterSelection) {
      navigate(`/checkout/details?${new URLSearchParams({ ...baseParams, selected_characters: '' }).toString()}`);
    } else {
      navigate(`/choose-character?${new URLSearchParams(baseParams).toString()}`);
    }
  };

  const occasions = [
    { title: 'Birthdays', desc: 'A gift that unfolds over time — each letter a new chapter in their story.', icon: 'M21 15.546c-.523 0-1.046.151-1.5.454a2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0A1.75 1.75 0 003 16.5v2.25A1.75 1.75 0 004.75 20.5h14.5A1.75 1.75 0 0021 18.75v-2.704' },
    { title: 'Long-Distance Loved Ones', desc: 'Bridge the distance with letters that connect hearts across any divide.', icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z' },
    { title: 'Book Lovers', desc: 'For anyone who loses themselves in stories — give them one that writes to them.', icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' },
    { title: 'Just Because', desc: 'No occasion needed — surprise someone with letters from another world.', icon: 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3' },
  ];

  return (
    <div className="bg-charcoal-900 min-h-screen pt-24" data-testid="gift-page">
      {/* Hero */}
      <section ref={heroRef} className="py-16 md:py-24">
        <div className={`max-w-3xl mx-auto px-6 text-center transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <p className="font-serif text-gold-400 tracking-[0.3em] text-xs mb-4">THE GIFT OF STORY</p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-parchment-100 mb-6" data-testid="gift-page-title">
            Give the Gift of Letters
          </h1>
          <p className="font-serif text-parchment-300 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-10">
            Some gifts are opened once and forgotten. This one arrives again and again — handwritten letters from characters who remember, who wonder, who wait for a reply. Give someone a story that becomes theirs.
          </p>
          <button
            onClick={() => setShowGiftModal(true)}
            data-testid="gift-hero-cta"
            className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-gold-600 to-gold-500 hover:from-gold-500 hover:to-gold-400 text-parchment-100 font-display text-sm tracking-widest uppercase rounded transition-all duration-300 shadow-lg hover:shadow-gold-600/20"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
            </svg>
            Send a Letter as a Gift
          </button>
        </div>
      </section>

      {/* How Gifting Works */}
      <section className="py-16 md:py-20 border-t border-gold-500/5">
        <div className="max-w-5xl mx-auto px-6">
          <p className="font-serif text-gold-400 tracking-[0.3em] text-xs mb-4 text-center">HOW IT WORKS</p>
          <h2 className="font-display text-2xl md:text-3xl text-parchment-100 mb-12 text-center">Three Simple Steps</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Choose a Subscription', desc: 'Pick a membership tier that suits the recipient — a single letter, monthly correspondence, or the complete chronicle.' },
              { step: '2', title: 'Add Their Details', desc: 'Enter the recipient\'s email and include a personal message. They\'ll receive a beautifully presented notification.' },
              { step: '3', title: 'They Begin Their Journey', desc: 'The recipient chooses their character and starts receiving letters from another world. The story is theirs.' },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gold-500/10 border border-gold-500/30 flex items-center justify-center">
                  <span className="font-display text-lg text-gold-400">{item.step}</span>
                </div>
                <h3 className="font-display text-lg text-parchment-100 mb-2">{item.title}</h3>
                <p className="font-serif text-parchment-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Occasions */}
      <section className="py-16 md:py-20 border-t border-gold-500/5">
        <div className="max-w-5xl mx-auto px-6">
          <p className="font-serif text-gold-400 tracking-[0.3em] text-xs mb-4 text-center">PERFECT FOR</p>
          <h2 className="font-display text-2xl md:text-3xl text-parchment-100 mb-12 text-center">Every Occasion, Every Heart</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {occasions.map((occasion) => (
              <div key={occasion.title} className="bg-charcoal-800/50 border border-gold-500/10 rounded-lg p-6 hover:border-gold-500/25 transition-colors" data-testid={`occasion-${occasion.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-gold-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={occasion.icon} />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-display text-lg text-parchment-100 mb-1">{occasion.title}</h3>
                    <p className="font-serif text-parchment-400 text-sm leading-relaxed">{occasion.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Available Gift Tiers */}
      <section className="py-16 md:py-20 border-t border-gold-500/5">
        <div className="max-w-4xl mx-auto px-6">
          <p className="font-serif text-gold-400 tracking-[0.3em] text-xs mb-4 text-center">GIFT OPTIONS</p>
          <h2 className="font-display text-2xl md:text-3xl text-parchment-100 mb-10 text-center">Choose a Gift</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {memberships.filter(m => m.is_giftable !== false).map((tier) => (
              <button
                key={tier.id}
                onClick={() => { setSelectedGiftTier(tier); setShowGiftModal(true); }}
                data-testid={`gift-tier-${tier.id}`}
                className="text-left bg-charcoal-800/60 border border-gold-500/20 hover:border-gold-500/40 rounded-lg p-5 transition-all duration-300 group"
              >
                <h3 className="font-display text-lg text-parchment-100 group-hover:text-gold-400 transition-colors mb-1">{tier.name}</h3>
                <p className="font-serif text-parchment-500 text-xs mb-3">
                  {tier.subscription_type === 'physical' ? 'Physical' : 'Digital'} • {tier.billing_cycle === 'one_time' ? 'One-time' : 'Monthly'}
                </p>
                <p className="font-serif text-parchment-400 text-sm mb-4 line-clamp-2">{tier.description || 'A unique storytelling experience'}</p>
                <div className="flex items-center justify-between">
                  <span className="font-display text-xl text-gold-400">${tier.price}</span>
                  <span className="font-serif text-gold-400 text-sm group-hover:text-gold-300 flex items-center gap-1">
                    Gift This
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 md:py-24 border-t border-gold-500/5">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="font-display text-2xl md:text-3xl text-parchment-100 mb-4">Ready to Send a Gift?</h2>
          <p className="font-serif text-parchment-400 leading-relaxed mb-8">
            Choose a subscription, add their details, and let the story begin. The perfect gift for anyone who believes in the magic of the written word.
          </p>
          <button
            onClick={() => setShowGiftModal(true)}
            data-testid="gift-bottom-cta"
            className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-gold-600 to-gold-500 hover:from-gold-500 hover:to-gold-400 text-parchment-100 font-display text-sm tracking-widest uppercase rounded transition-all duration-300 shadow-lg hover:shadow-gold-600/20"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
            </svg>
            Send a Letter as a Gift
          </button>
        </div>
      </section>

      <SiteFooter />

      {/* Gift Modal */}
      {showGiftModal && (
        <div className="fixed inset-0 bg-charcoal-900/90 z-50 flex items-center justify-center p-4">
          <div className="bg-charcoal-800 border border-gold-500/20 rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-display text-2xl text-parchment-100">Gift a Subscription</h3>
                <button onClick={() => { setShowGiftModal(false); setSelectedGiftTier(null); setGiftForm({ email: '', message: '' }); }} className="text-parchment-400 hover:text-parchment-100">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {!selectedGiftTier ? (
                <>
                  <p className="font-serif text-parchment-300 mb-6">Choose a subscription tier to gift:</p>
                  <div className="space-y-3">
                    {memberships.filter(m => m.is_giftable !== false).map((tier) => (
                      <button key={tier.id} onClick={() => setSelectedGiftTier(tier)} className="w-full p-4 bg-charcoal-700/50 hover:bg-charcoal-700 border border-gold-500/10 hover:border-gold-500/30 rounded text-left transition-all">
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="font-display text-lg text-parchment-100">{tier.name}</h4>
                            <p className="font-serif text-parchment-400 text-sm">{tier.description || `${tier.character_count || 1} character${(tier.character_count || 1) > 1 ? 's' : ''}`}</p>
                          </div>
                          <span className="font-display text-xl text-gold-400">${tier.price}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-4 p-3 bg-charcoal-700/30 rounded">
                    <p className="font-serif text-parchment-400 text-sm">Selected: <span className="text-gold-400">{selectedGiftTier.name}</span></p>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block font-serif text-parchment-300 text-sm mb-2">Recipient's Email *</label>
                      <input type="email" value={giftForm.email} onChange={(e) => setGiftForm({ ...giftForm, email: e.target.value })} placeholder="friend@email.com" className="w-full bg-charcoal-700 border border-gold-500/20 rounded px-4 py-3 text-parchment-100 font-serif focus:outline-none focus:border-gold-500/50" required />
                    </div>
                    <div>
                      <label className="block font-serif text-parchment-300 text-sm mb-2">Gift Message (optional)</label>
                      <textarea value={giftForm.message} onChange={(e) => setGiftForm({ ...giftForm, message: e.target.value })} placeholder="A personal note to include with the gift..." rows={3} className="w-full bg-charcoal-700 border border-gold-500/20 rounded px-4 py-3 text-parchment-100 font-serif focus:outline-none focus:border-gold-500/50 resize-none" />
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6">
                    <button onClick={() => setSelectedGiftTier(null)} className="flex-1 font-serif py-3 border border-gold-500/40 text-parchment-100 rounded hover:border-gold-400 transition-colors">Back</button>
                    <button onClick={() => { if (!giftForm.email) { alert('Please enter recipient email'); return; } handleSelectTier(selectedGiftTier, { is_gift: true, gift_recipient_email: giftForm.email, gift_message: giftForm.message }); setShowGiftModal(false); }} data-testid="gift-page-continue-btn" className="flex-1 font-serif py-3 bg-gold-500 hover:bg-gold-400 text-parchment-100 rounded transition-colors">Choose Characters</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== ADMIN WRAPPER ==============
const AdminWrapper = () => {
  return <AdminDashboard />;
};

// ============== MAIN APP ==============
function App() {
  const isAdminRoute = window.location.pathname.startsWith('/admin');

  return (
    <div className="App">
      <BrowserRouter>
        {!isAdminRoute && <Navigation />}
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/membership" element={<Membership />} />
          <Route path="/gift" element={<GiftPage />} />
          <Route path="/free-letter-kit" element={<FreeLetterKitPage />} />
          <Route path="/choose-character" element={<ChooseCharacter />} />
          <Route path="/characters" element={<Characters />} />
          <Route path="/characters/:slug" element={<CharacterPage />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/about" element={<About />} />
          <Route path="/author" element={<AboutAuthor />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/reviews" element={<ReaderReflections />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/shipping-policy" element={<ShippingPolicy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/checkout/details" element={<PreCheckout />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/checkout/cancel" element={<Membership />} />
          <Route path="/pages/:slug" element={<CustomPage />} />
          <Route path="/admin/*" element={<AdminWrapper />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
